/** Types des réponses de l'API FONAREV 360 (miroir des DTO et services NestJS). */

export type Profil =
  | 'EMETTEUR'
  | 'ASSISTANT'
  | 'CHEF_SERVICE'
  | 'CONSEILLER_TECHNIQUE'
  | 'DIRECTEUR'
  | 'COORDONNATEUR_MEMORIAL'
  | 'CONTROLE_GESTION'
  | 'DIRECTEUR_FINANCIER'
  | 'DGA_OPERATIONS'
  | 'DGA_FINANCES'
  | 'DIRECTEUR_GENERAL'
  | 'TRESORERIE'
  | 'TRESORIER'
  | 'CAISSIER'
  | 'CHEF_COMPTABLE'
  | 'CHARGE_ACHATS'
  | 'RESPONSABLE_ACHATS'
  | 'CELLULE_MARCHES'
  | 'DIRECTEUR_RH'
  | 'RESSOURCES_HUMAINES'
  | 'INVESTISSEMENTS'
  | 'PAIE'
  | 'ADMINISTRATEUR';

export type Etape =
  | 'DIRECTEUR'
  | 'COORDONNATEUR_MEMORIAL'
  | 'CONTROLE_GESTION'
  | 'DIRECTEUR_FINANCIER'
  | 'DGA_OPERATIONS'
  | 'DGA_FINANCES'
  | 'DIRECTEUR_GENERAL'
  | 'RESPONSABLE_ACHATS'
  | 'CELLULE_MARCHES'
  | 'SUPERIEUR_HIERARCHIQUE'
  | 'RESSOURCES_HUMAINES';

export const LIBELLE_ETAPE: Record<Etape, string> = {
  DIRECTEUR: 'Directeur',
  COORDONNATEUR_MEMORIAL: 'Coordonnateur du Mémorial',
  CONTROLE_GESTION: 'Contrôle de gestion',
  DIRECTEUR_FINANCIER: 'Directeur financier',
  DGA_OPERATIONS: 'DGA Opérations',
  DGA_FINANCES: 'DGA Finances',
  DIRECTEUR_GENERAL: 'Directeur Général',
  RESPONSABLE_ACHATS: 'Responsable des achats',
  CELLULE_MARCHES: 'Cellule des marchés',
  SUPERIEUR_HIERARCHIQUE: 'Supérieur hiérarchique',
  RESSOURCES_HUMAINES: 'Directeur RH et Juridique',
};

export type StatutDocument = 'BROUILLON' | 'SOUMIS' | 'A_CORRIGER' | 'AUTORISE' | 'REJETE' | 'ANNULE';

export const LIBELLE_STATUT: Record<StatutDocument, string> = {
  BROUILLON: 'Brouillon',
  SOUMIS: 'En validation',
  A_CORRIGER: 'À corriger',
  AUTORISE: 'Autorisé',
  REJETE: 'Rejeté',
  ANNULE: 'Annulé',
};

export type DecisionApi = 'VALIDER' | 'COMMENTER' | 'CORRIGER' | 'REJETER' | 'ANNULER';

export const LIBELLE_DECISION: Record<DecisionApi, string> = {
  VALIDER: 'Valider',
  COMMENTER: 'Commenter',
  CORRIGER: 'Demander une correction',
  REJETER: 'Rejeter',
  ANNULER: 'Annuler le document',
};

export interface Habilitation {
  profil: Profil;
  structureId: string | null;
  structureCode: string | null;
  serviceId: string | null;
  serviceCode: string | null;
  titulaireId: string | null;
}

export interface Principal {
  agentId: string;
  matricule: string;
  nom: string;
  prenom: string;
  email: string;
  fonction: string;
  structureId: string;
  structureCode: string;
  categorie: string;
  base: 'SIEGE' | 'COORDINATION';
  province: string | null;
  source: 'ENTRA' | 'LOCAL';
  habilitations: Habilitation[];
  profils: Profil[];
  etapes: Etape[];
}

export interface ResultatConnexion {
  jeton: string;
  expireLe: string;
  utilisateur: Principal;
  doitChangerMotDePasse?: boolean;
}

export interface Configuration {
  microsoft: boolean;
  tenantId: string;
  clientId: string;
  authority: string | null;
  comptesLocaux: boolean;
  /** Connexion locale réservée aux administrateurs tant que le mode de secours n'est pas activé. */
  connexionLocaleRestreinte: boolean;
  profils: Array<{ code: Profil; libelle: string }>;
}

export interface Acteur {
  userId: string;
  name: string;
  onBehalfOfUserId?: string;
}

export interface EtapeCircuit {
  key: Etape;
  status: 'WAITING' | 'CURRENT' | 'DONE' | 'RETURNED_FOR_CORRECTION' | 'REVALIDATION_REQUESTED';
  signedBy: Acteur | null;
  signedAt: string | null;
  receivedAt: string | null;
  modifiedAfterValidation: boolean;
  /** Validation invalidée à la suite de l'annulation automatique du document. */
  invalidated?: boolean;
}

/** Échéance d'une demande de correction, en jours ouvrables (lundi au vendredi), spécifications complémentaires v1.1 § 5. */
export interface EcheanceCorrection {
  demandeeLe: string;
  dateLimite: string;
  joursOuvrablesRestants: number;
  delaiJoursOuvrables: number;
  expiree: boolean;
}

export interface DecisionCircuit {
  kind: 'SUBMIT' | 'VALIDATE' | 'COMMENT' | 'REQUEST_CORRECTION' | 'REJECT' | 'CANCEL' | 'RESUBMIT' | 'REQUEST_REVALIDATION' | 'EXPIRE';
  step: Etape | null;
  actor: Acteur;
  at: string;
  comment: string | null;
  steps?: Etape[];
}

export interface Circuit {
  context: { type: string; amount: number; isOperationalDirection?: boolean; isMemorialActivity?: boolean };
  status: 'DRAFT' | 'IN_VALIDATION' | 'RETURNED_FOR_CORRECTION' | 'AUTHORIZED' | 'REJECTED' | 'CANCELLED';
  returnedBy: Etape | null;
  steps: EtapeCircuit[];
  history: DecisionCircuit[];
}

export interface Actions {
  modifier: boolean;
  soumettre: boolean;
  resoumettre: boolean;
  decider: boolean;
  etape: Etape | null;
  decisions: DecisionApi[];
  revalidation: boolean;
}

export interface Controle {
  ok: boolean;
  message: string;
}

export interface Structure {
  id: string;
  code: string;
  libelle: string;
  type: string;
  operationnelle: boolean;
  superviseur: string;
  actif: boolean;
}

export interface Agent {
  id: string;
  matricule: string;
  nom: string;
  prenom: string;
  email: string;
  fonction: string;
  structureId: string;
  structureCode: string | null;
  categorie: string;
  base: 'SIEGE' | 'COORDINATION';
  province: string | null;
  microsoft: boolean;
}

export interface LigneCout {
  equipe: number;
  agentId: string;
  typeMission: 'INTER' | 'INTRA' | 'EXT';
  nature: string;
  description: string;
  montant: number;
}

export interface ResultatCalcul {
  baremeVersion: string;
  lignes: LigneCout[];
  total: number;
  parTypeMission: Partial<Record<'INTER' | 'INTRA' | 'EXT', number>>;
  parEquipe: number[];
  parAgent: Record<string, number>;
  controles: Array<Controle & { niveau: number }>;
}

export interface Destination {
  province: string;
  localite: string;
}

export interface ContenuTdr {
  contexte: string;
  objectifGlobal: string;
  objectifsSpecifiques: string[];
  resultats: string[];
  methodologie: string;
  livrables: string[];
  cible: string;
  hebergementParAchats?: boolean;
}

export interface AutreFrais {
  description: string;
  montant: number;
}

export interface MembreTdr {
  id: string;
  agentId: string;
  chef: boolean;
  agent: Agent;
}

export interface EquipeTdr {
  id: string;
  ordre: number;
  nom: string;
  typeMission: 'INTER' | 'INTRA' | 'EXT';
  provinceDepart: string;
  debut: string | null;
  fin: string | null;
  transports: string[];
  destinations: Destination[];
  membres: MembreTdr[];
  /** Programme jour par jour (étape « Agenda ») ; null tant qu'il n'est pas décrit. */
  agenda: AgendaJour[] | null;
}

export interface AgendaJour {
  date: string;
  description: string;
  lieu?: string;
}

export interface LigneBudgetaireRef {
  id: string;
  code: string;
  libelle: string;
}

export interface Tdr {
  id: string;
  numero: string | null;
  titre: string;
  structureId: string;
  structure?: Structure;
  ligneBudgetaireId: string;
  ligneBudgetaire?: LigneBudgetaireRef & { budgetActualise?: number };
  type: 'MISSION' | 'FORMATION' | 'AUTRE';
  zone: 'INT' | 'EXT';
  urgence: boolean;
  justificationUrgence: string | null;
  statut: StatutDocument;
  montantA: number;
  montantB: number;
  baremeVersion: string;
  emetteurId: string;
  emetteur?: Agent;
  circuitInstanceId: string | null;
  contenu: ContenuTdr;
  memorial: boolean;
  autresFrais: AutreFrais[] | null;
  calcul: ResultatCalcul | null;
  semaineDecaissement: string | null;
  dateSoumission: string | null;
  /** Dernière demande de correction en attente de réponse (délai d'annulation automatique). */
  correctionDemandeeLe: string | null;
  motifAnnulation: string | null;
  annulationAutomatique: boolean;
  dateAutorisation: string | null;
  revisions: Array<{ note: string; date: string; montantAncien: number; montantNouveau: number; montantA: number; montantB: number }> | null;
  equipes: EquipeTdr[];
  createdAt: string;
  updatedAt: string;
}

export interface DetailTdr {
  tdr: Tdr;
  calcul: ResultatCalcul | null;
  montantTotal: number;
  circuit: Circuit | null;
  etapeCourante: Etape | null;
  actions: Actions;
  controles: Controle[];
  feds: Array<{ id: string; numero: string | null; statut: StatutDocument; montantUsd: number; etatPaiement: EtatPaiement }>;
  /** Échéance de la correction attendue (document à corriger), sinon null. */
  correction: EcheanceCorrection | null;
}

export interface TdrResume {
  id: string;
  numero: string | null;
  titre: string;
  type: string;
  zone: string;
  statut: StatutDocument;
  etapeCourante: Etape | null;
  urgence: boolean;
  memorial: boolean;
  montant: number;
  structureCode: string | null;
  structureLibelle: string | null;
  ligneBudgetaire: LigneBudgetaireRef | null;
  emetteur: { id: string; nom: string } | null;
  dateSoumission: string | null;
  correction: EcheanceCorrection | null;
  motifAnnulation: string | null;
  annulationAutomatique: boolean;
  dateAutorisation: string | null;
  updatedAt: string;
}

export type EtatPaiement = 'A_PAYER' | 'LIQUIDE' | 'PAYE';

export const LIBELLE_PAIEMENT: Record<EtatPaiement, string> = { A_PAYER: 'À payer', LIQUIDE: 'Mis en paiement', PAYE: 'Payé' };

export type NatureFed = 'PERDIEMS' | 'FOURNISSEUR' | 'AUTRE' | 'RETOUR_CAISSE' | 'NIVELLEMENT';

export const LIBELLE_NATURE: Record<NatureFed, string> = {
  PERDIEMS: 'Frais de mission',
  FOURNISSEUR: 'Facture fournisseur',
  AUTRE: 'Autre dépense',
  RETOUR_CAISSE: 'Retour de caisse',
  NIVELLEMENT: 'Nivellement',
};

export interface FedLigne {
  id: string;
  ordre: number;
  description: string;
  beneficiaireType: 'AGENT' | 'FOURNISSEUR';
  agentId: string | null;
  agent?: Agent | null;
  beneficiaireLibelle: string | null;
  montant: number;
}

export interface Fed {
  id: string;
  numero: string | null;
  tdrId: string | null;
  tdr?: Tdr | null;
  structureId: string;
  structure?: Structure;
  ligneBudgetaireId: string;
  ligneBudgetaire?: LigneBudgetaireRef;
  objet: string;
  nature: NatureFed;
  montant: number;
  devise: string;
  montantUsd: number;
  statut: StatutDocument;
  modePaiement: string | null;
  semaineDecaissement: string | null;
  urgence: boolean;
  memorial: boolean;
  dateSoumission: string | null;
  /** Dernière demande de correction en attente de réponse (délai d'annulation automatique). */
  correctionDemandeeLe: string | null;
  motifAnnulation: string | null;
  annulationAutomatique: boolean;
  dateAutorisation: string | null;
  etatPaiement: EtatPaiement;
  datePaiement: string | null;
  suspendu: boolean;
  bonCommandeId: string | null;
  emetteurId: string;
  emetteur?: Agent;
  circuitInstanceId: string | null;
  lignes: FedLigne[];
  createdAt: string;
  updatedAt: string;
}

export interface Verification {
  ligneId: string;
  code: string;
  montant: number;
  disponible: number;
  disponiblePrevisionnel: number;
  ok: boolean;
  previsionnelOk: boolean;
  depassement: number;
  message: string;
}

export interface DetailFed {
  fed: Fed;
  circuit: Circuit | null;
  etapeCourante: Etape | null;
  actions: Actions & { liquider: boolean; payer: boolean };
  controles: Controle[];
  budget: Verification | null;
  tdr: { id: string; numero: string | null; titre: string; statut: StatutDocument } | null;
  bonCommande: { id: string; numero: string | null; objet: string; fournisseur: string; montantUsd: number } | null;
  /** Échéance de la correction attendue (document à corriger), sinon null. */
  correction: EcheanceCorrection | null;
}

export interface FedResume {
  id: string;
  numero: string | null;
  objet: string;
  nature: NatureFed;
  statut: StatutDocument;
  etapeCourante: Etape | null;
  etatPaiement: EtatPaiement;
  urgence: boolean;
  montant: number;
  devise: string;
  montantUsd: number;
  structureCode: string | null;
  structureLibelle: string | null;
  ligneBudgetaire: LigneBudgetaireRef | null;
  tdr: { id: string; numero: string | null; titre: string } | null;
  emetteur: { id: string; nom: string } | null;
  semaineDecaissement: string | null;
  dateSoumission: string | null;
  correction: EcheanceCorrection | null;
  motifAnnulation: string | null;
  annulationAutomatique: boolean;
  dateAutorisation: string | null;
  datePaiement: string | null;
  updatedAt: string;
}

export interface SituationLigne {
  id: string;
  exercice: number;
  code: string;
  libelle: string;
  structureId: string;
  structureCode: string;
  structureLibelle: string;
  programme: string;
  /** Activité de rattachement (la ligne est la sous-activité) ; null : non classée. */
  activiteCode: string | null;
  activiteLibelle: string | null;
  serviceCode: string | null;
  serviceLibelle: string | null;
  source: string;
  projet: boolean;
  budgetInitial: number;
  budgetActualise: number;
  engage: number;
  liquide: number;
  paye: number;
  disponible: number;
  enCircuit: number;
  disponiblePrevisionnel: number;
  tauxEngagement: number;
  tauxExecution: number;
  alertes: string[];
  /** Faux : sous-activité retirée de la dernière version approuvée du PTBA (conservée avec son exécution, fermée à l'initiation). */
  actif: boolean;
  /** Ligne de réserve (provision pour imprévus de l'entité) : mobilisable par virement seulement. */
  reserve: boolean;
  programmation: ProgrammationLigne;
}

/** Attributs du plan opérationnel hérités du PTBA (affichés en lecture dans les formulaires). */
export interface ProgrammationLigne {
  axeCode: string | null;
  objectifCode: string | null;
  objectifLibelle: string | null;
  resultatAttendu: string | null;
  kpi: string | null;
  cibleValeur: number | null;
  cibleUnite: string | null;
  cibleType: 'CUMULE' | 'PERIODIQUE' | 'POURCENTAGE' | 'BINAIRE' | null;
  cibleTexte: string | null;
  livrable: string | null;
  lieux: string[];
  lieuPrecision: string | null;
  responsableLibelle: string | null;
  responsableAgentId: string | null;
  partenaires: string | null;
  debutPrevu: string | null;
  finPrevue: string | null;
  versionId: string | null;
}

/** Ligne de dépense du PTBA rattachée à une sous-activité (informative). */
export interface LigneDepense {
  id: string;
  code: string;
  natureCode: string;
  natureLibelle: string | null;
  categorie: string | null;
  quantite: number;
  frequence: number;
  coutUnitaire: number;
  budget: number;
  planMensuel: number[] | null;
  observations: string | null;
  actif: boolean;
}

export interface DetailLigneBudgetaire extends SituationLigne {
  mouvements: Mouvement[];
  lignesDepense: LigneDepense[];
  version: { id: string; numero: string; statut: StatutVersionPtba; dateDecision: string | null } | null;
}

// ------------------------------------------------------------------ versions du PTBA (import de l'onglet « Export 360 »)
export type StatutVersionPtba = 'BROUILLON' | 'SOUMIS' | 'APPROUVE' | 'REMPLACE' | 'REJETE';
export const LIBELLE_STATUT_VERSION: Record<StatutVersionPtba, string> = { BROUILLON: 'Brouillon', SOUMIS: 'Soumise', APPROUVE: 'Approuvée', REMPLACE: 'Remplacée', REJETE: 'Rejetée' };

export interface AnomaliePtba {
  ligne: number;
  code: string | null;
  message: string;
}

export type NatureChangementPtba = 'NOUVELLE' | 'MODIFIEE' | 'INCHANGEE' | 'ABSENTE' | 'RENOMMEE' | 'REAFFECTEE' | 'REDUITE';
export const LIBELLE_CHANGEMENT_PTBA: Record<NatureChangementPtba, string> = { NOUVELLE: 'Nouvelle', MODIFIEE: 'Modifiée', INCHANGEE: 'Inchangée', ABSENTE: 'Absente (désactivée)', RENOMMEE: 'Renommée', REAFFECTEE: 'Réaffectée', REDUITE: 'Réduite sous l’engagé' };

export interface ChangementPtba {
  type: 'SOUS_ACTIVITE' | 'LIGNE' | 'PROVISION';
  code: string;
  nature: NatureChangementPtba;
  libelle: string;
  detail?: string;
  budgetAvant?: number;
  budgetApres?: number;
}

export interface TotalEntitePtba {
  entite: string;
  structure: string | null;
  lignes: number;
  budget: number;
  provision: number;
  totalFiche: number | null;
  ecart: number | null;
}

export interface RapportPtba {
  exercice: number;
  simulation: boolean;
  origine: string;
  version: { numero: string | null; statut: string | null; date: string | null; auteur: string | null };
  lignes: { lues: number; conformes: number; provisions: number; totaux: number; anomaliesClasseur: number };
  sousActivites: { nouvelles: number; modifiees: number; inchangees: number; absentes: number; renommees: number; reaffectees: number };
  budget: { version: number; approuve: number | null; ecart: number | null };
  totauxEntites: TotalEntitePtba[];
  changements: ChangementPtba[];
  erreurs: AnomaliePtba[];
  avertissements: AnomaliePtba[];
  enregistree: boolean;
  versionId: string | null;
  statut: StatutVersionPtba | null;
}

export interface VersionPtba {
  id: string;
  exercice: number;
  numero: string;
  statut: StatutVersionPtba;
  approbationClasseur: boolean;
  source: string | null;
  dateImport: string;
  importeParNom: string;
  dateDecision: string | null;
  decideParNom: string | null;
  motif: string | null;
  budget: number;
  sousActivites: number;
  lignes: number;
  ecartApprouve: number | null;
  erreurs: number;
  avertissements: number;
}

export interface DetailVersionPtba extends Omit<VersionPtba, 'lignes'> {
  rapport: RapportPtba;
  nombreLignes: number;
  lignes: Array<{ type: 'SOUS_ACTIVITE' | 'LIGNE' | 'PROVISION'; code: string; budget: number; donnees: Record<string, unknown> }>;
}

// ------------------------------------------------------------------ plan opérationnel (suivi des sous-activités)
export type MethodePlan = 'INDICATEUR' | 'JALONS' | 'BINAIRE';
export type StatutPlan = 'PREVUE' | 'NON_DEMARREE' | 'EN_COURS' | 'TERMINEE' | 'SUSPENDUE' | 'ANNULEE';
export type TypeCiblePlan = 'CUMULE' | 'PERIODIQUE' | 'POURCENTAGE' | 'BINAIRE';
export type StatutJalon = 'A_VENIR' | 'ATTEINT_DECLARE' | 'ATTEINT_VALIDE';
export type EtatMiseAJourPlan = 'SAISIE' | 'SOUMISE' | 'VALIDEE' | 'COMPLEMENT_DEMANDE';
export const LIBELLE_STATUT_PLAN: Record<StatutPlan, string> = { PREVUE: 'Prévue', NON_DEMARREE: 'Non démarrée', EN_COURS: 'En cours', TERMINEE: 'Terminée', SUSPENDUE: 'Suspendue', ANNULEE: 'Annulée' };
export const LIBELLE_METHODE_PLAN: Record<MethodePlan, string> = { INDICATEUR: 'Indicateur (cible)', JALONS: 'Jalons pondérés', BINAIRE: 'Réalisée / non réalisée' };
export const LIBELLE_TYPE_CIBLE: Record<TypeCiblePlan, string> = { CUMULE: 'Cumulée sur l’exercice', PERIODIQUE: 'Par période (mois)', POURCENTAGE: 'Pourcentage', BINAIRE: 'Oui / non' };
export const LIBELLE_ETAT_MAJ: Record<EtatMiseAJourPlan, string> = { SAISIE: 'En saisie', SOUMISE: 'Soumise', VALIDEE: 'Validée', COMPLEMENT_DEMANDE: 'Complément demandé' };
export const LIBELLE_STATUT_JALON: Record<StatutJalon, string> = { A_VENIR: 'À venir', ATTEINT_DECLARE: 'Déclaré atteint', ATTEINT_VALIDE: 'Atteint (validé)' };

export interface RetardPlan {
  enRetard: boolean;
  jours: number;
  motif: string | null;
}

export interface DroitsPlan {
  mettreAJour: boolean;
  exceptionMiseAJour: boolean;
  valider: boolean;
  exceptionValidation: boolean;
  parametrer: boolean;
}

export interface ElementPlan {
  ligneId: string;
  exercice: number;
  code: string;
  libelle: string;
  structureId: string;
  structureCode: string;
  structureLibelle: string;
  serviceId: string | null;
  activiteCode: string | null;
  activiteLibelle: string | null;
  axeCode: string | null;
  objectifCode: string | null;
  lieux: string[];
  kpi: string | null;
  cibleTexte: string | null;
  cibleUnite: string | null;
  responsable: { id: string; nom: string } | null;
  responsableLibelle: string | null;
  methode: MethodePlan;
  typeCible: TypeCiblePlan | null;
  cibleValeur: number | null;
  ciblePeriode: number | null;
  frequenceJours: number;
  modePaiement: 'AVANCE' | 'LIVRAISON' | null;
  poids: number | null;
  statut: StatutPlan;
  avancement: number | null;
  valeurValidee: number | null;
  retard: RetardPlan;
  fraicheurJours: number | null;
  derniereValidation: string | null;
  derniereMiseAJour: string | null;
  echeance: string;
  aMettreAJour: boolean;
  enAttente: number;
  complementsDemandes: number;
  saisiesEnCours: number;
  jalons: { total: number; valides: number; echus: number };
  debutPrevu: string | null;
  finPrevue: string | null;
  debutEffectif: string | null;
  finEffective: string | null;
  reprogrammee: boolean;
  financier: { budgetInitial: number; budgetActualise: number; engage: number; paye: number; enCircuit: number; disponible: number; tauxEngagement: number; tauxExecution: number };
  comparaison: { ecart: number | null; aExaminer: boolean; calendaire: number | null };
  /** Indicateur lu depuis Centralized ou le suivi des réparations ; null si la valeur est saisie. */
  indicateurExterne: IndicateurLiePlan | null;
  aParametrer: boolean;
  actifSuivi: boolean;
  droits: DroitsPlan;
}

export interface IndicateurLiePlan {
  code: string;
  libelle: string;
  source: 'CENTRALIZED' | 'REPARATIONS';
  sourceLibelle: string;
  mode: 'PERIODE' | 'CUMUL';
  valeur: number | null;
  periodeDebut: string | null;
  periodeFin: string | null;
  actualiseLe: string | null;
  etat: 'NON_CONNECTE' | 'A_JOUR' | 'OBSOLETE' | 'EN_ERREUR';
  message: string | null;
}

export interface ElementCourtPlan {
  ligneId: string;
  code: string;
  libelle: string;
  structureCode: string;
  echeance?: string;
  jours?: number;
}

/** Bloc « Plan opérationnel » du tableau de bord, composé selon le profil. */
export interface BlocPlanTableau {
  exercice: number;
  profil: 'DIRECTION' | 'DGA' | 'DF' | 'CG' | 'DIR' | 'CM' | 'RESPONSABLE';
  perimetre: string;
  synthese: ConsolidationPlan;
  entites: Array<{ structureCode: string; structureLibelle: string; total: number; renseignees: number; avancement: number | null; tauxExecution: number; ecart: number | null; retards: number; enAttente: number; aMettreAJour: number; aExaminer: number }>;
  activites: Array<{ activiteCode: string | null; activiteLibelle: string | null; total: number; renseignees: number; avancement: number | null; retards: number; enAttente: number; aMettreAJour: number }>;
  miennes: { aMettreAJour: ElementCourtPlan[]; complements: ElementCourtPlan[]; retards: ElementCourtPlan[]; enAttente: ElementCourtPlan[]; justificatifsAttendus: ElementCourtPlan[] };
  difficultes: Array<ElementCourtPlan & { periode: string; texte: string }>;
  besoins: Array<{ mois: number; libelle: string; montant: number; sousActivites: number }>;
  qualite: { couverture: number; fraicheurMoyenneJours: number | null; sansMethode: number; sansResponsable: number; ecarts: number; nonRenseignees: number };
}

export interface ConsolidationPlan {
  avancement: number | null;
  couverture: number;
  renseignees: number;
  total: number;
  parStatut: Record<StatutPlan, number>;
  retards: number;
  enAttente: number;
  aMettreAJour: number;
  aExaminer: number;
  budgetActualise: number;
  engage: number;
  paye: number;
  tauxExecution: number;
}

export interface SynthesePlan {
  ponderation: 'EGALE' | 'BUDGET';
  total: ConsolidationPlan;
  structures: Array<ConsolidationPlan & { structureId: string; structureCode: string; structureLibelle: string; activites: Array<ConsolidationPlan & { activiteCode: string | null; activiteLibelle: string | null }> }>;
}

export interface ListePlan {
  exercice: number;
  aujourdhui: string;
  lignes: ElementPlan[];
  synthese: SynthesePlan;
}

export interface FiltresPlan {
  exercice: number;
  structure?: string;
  activite?: string;
  lieu?: string;
  responsable?: string;
  statut?: StatutPlan;
  retard?: boolean;
  aMettreAJour?: boolean;
  enAttente?: boolean;
  aParametrer?: boolean;
  ponderation?: 'EGALE' | 'BUDGET';
}

export interface JalonPlan {
  id: string;
  ordre: number;
  libelle: string;
  poids: number;
  datePrevue: string | null;
  dateRealisee: string | null;
  statut: StatutJalon;
  pieceId: string | null;
}

export interface SuiviPlan {
  id: string;
  ligneId: string;
  statut: StatutPlan;
  methode: MethodePlan;
  frequenceJours: number;
  typeCible: TypeCiblePlan | null;
  cibleValeur: number | null;
  ciblePeriode: number | null;
  modePaiement: 'AVANCE' | 'LIVRAISON' | null;
  poids: number | null;
  responsableAgentId: string | null;
  debutPrevu: string | null;
  finPrevue: string | null;
  debutEffectif: string | null;
  finEffective: string | null;
  avancementPhysique: number | null;
  valeurValidee: number | null;
  derniereMiseAJour: string | null;
  derniereValidation: string | null;
  parametrePar: string | null;
  parametreLe: string | null;
}

export interface MiseAJourPlan {
  id: string;
  periode: string;
  date: string;
  auteurId: string;
  auteurNom: string;
  exception: boolean;
  statutDeclare: StatutPlan;
  valeurIndicateur: number | null;
  valeurEnCours: number | null;
  jalonsAtteints: string[] | null;
  realisations: string | null;
  difficultes: string | null;
  actionsCorrectives: string | null;
  pieces: string[] | null;
  valeurSource: { code: string; source: string; libelle: string; periodeDebut: string | null; periodeFin: string | null; actualiseLe: string | null; etat: string } | null;
  etat: EtatMiseAJourPlan;
  dateSoumission: string | null;
  valideParNom: string | null;
  dateValidation: string | null;
  commentaireValidateur: string | null;
}

export interface ReprogrammationPlan {
  id: string;
  date: string;
  auteurNom: string;
  ancienDebut: string | null;
  ancienneFin: string | null;
  nouveauDebut: string | null;
  nouvelleFin: string | null;
  retardAnterieurJours: number;
  motif: string;
  etat: 'PROPOSEE' | 'VALIDEE' | 'REFUSEE';
  valideParNom: string | null;
  dateDecision: string | null;
  commentaire: string | null;
}

export interface SituationPlanFigee {
  id: string;
  periode: string;
  date: string;
  statut: StatutPlan;
  avancement: number | null;
  valeur: number | null;
  retardJours: number;
  budgetActualise: number;
  engage: number;
  paye: number;
  tauxExecution: number;
}

export interface DocumentLiePlan {
  id: string;
  numero: string | null;
  objet: string;
  statut: StatutDocument;
  montant: number;
}

export interface FichePlan {
  aujourdhui: string;
  element: ElementPlan;
  ligne: DetailLigneBudgetaire;
  suivi: SuiviPlan | null;
  lien: { id: string; code: string; mode: 'PERIODE' | 'CUMUL'; creePar: string | null } | null;
  jalons: JalonPlan[];
  misesAJour: MiseAJourPlan[];
  periodes: Array<{ periode: string; taux: number | null }>;
  reprogrammations: ReprogrammationPlan[];
  situations: SituationPlanFigee[];
  documents: { tdr: DocumentLiePlan[]; fed: DocumentLiePlan[]; requisitions: DocumentLiePlan[]; bons: DocumentLiePlan[] };
}

export interface ParametragePlan {
  methode?: MethodePlan;
  frequenceJours?: number;
  typeCible?: TypeCiblePlan | null;
  cibleValeur?: number | null;
  ciblePeriode?: number | null;
  modePaiement?: 'AVANCE' | 'LIVRAISON' | null;
  poids?: number | null;
  responsableAgentId?: string | null;
  jalons?: Array<{ id?: string; libelle: string; poids: number; datePrevue?: string | null }>;
  indicateurExterne?: { code: string; mode?: 'PERIODE' | 'CUMUL' } | null;
}

export interface SaisieMiseAJourPlan {
  periode: string;
  statutDeclare: StatutPlan;
  valeurIndicateur?: number | null;
  valeurEnCours?: number | null;
  jalonsAtteints?: string[];
  realisations?: string | null;
  difficultes?: string | null;
  actionsCorrectives?: string | null;
  pieces?: string[];
  soumettre?: boolean;
}

export interface SituationsPlan {
  periode: string;
  date: string;
  synthese: { avancement: number | null; couverture: number; renseignees: number; total: number; parStatut: Record<StatutPlan, number>; retards: number; budgetActualise: number; engage: number; paye: number };
  lignes: Array<{ ligneId: string; code: string; libelle: string; structureCode: string; activiteCode: string | null; statut: StatutPlan; avancement: number | null; valeur: number | null; retardJours: number; budgetActualise: number; engage: number; paye: number; tauxExecution: number; ecart: number | null }>;
}

export interface VersionPtbaApprouvee {
  exercice: number;
  version: VersionPtba | null;
  /** Versions brouillon ou soumises en attente (affichage « version provisoire »). */
  provisoires: number;
}

export interface Mouvement {
  id: string;
  type: 'ENGAGEMENT' | 'LIQUIDATION' | 'PAIEMENT' | 'VIREMENT';
  montant: number;
  date: string;
  fedId: string | null;
  reference: string | null;
  libelle: string;
}

export interface TotauxBudget {
  lignes: number;
  budgetInitial: number;
  budgetActualise: number;
  engage: number;
  liquide: number;
  paye: number;
  disponible: number;
  enCircuit: number;
  disponiblePrevisionnel: number;
  tauxEngagement: number;
  tauxExecution: number;
}

export interface SyntheseStructure extends TotauxBudget {
  structureId: string;
  structureCode: string;
  structureLibelle: string;
  superviseur: string;
}

export interface Synthese {
  total: TotauxBudget;
  structures: SyntheseStructure[];
}

export type TypeDossier = 'TDR' | 'FED' | 'NE' | 'BC' | 'REQ';

/** Chemin de la page de détail d'un dossier du tableau de bord. */
export const cheminDossier = (type: TypeDossier, id: string): string => ({ TDR: '/tdr/', FED: '/fed/', NE: '/ne/', BC: '/achats/', REQ: '/requisitions/' })[type] + id;

export const LIBELLE_TYPE_DOSSIER: Record<TypeDossier, string> = { TDR: 'TDR', FED: 'FED', NE: 'Note explicative', BC: 'Bon de commande', REQ: 'Réquisition' };

export interface DossierATraiter {
  type: TypeDossier;
  id: string;
  numero: string | null;
  objet: string;
  montant: number;
  structureCode: string | null;
  emetteur: string | null;
  etape: Etape;
  depuis: string | null;
  heures: number;
  enRetard: boolean;
}

/** Indicateur reçu d'une application source (Centralized, suivi des réparations), v1.1 § 4. */
export interface IndicateurResultat {
  code: string;
  libelle: string;
  definition: string;
  unite: 'nombre' | 'usd' | 'pourcentage';
  famille: string;
  frequenceJours: number;
  reserve: boolean;
  etat: 'NON_CONNECTE' | 'A_JOUR' | 'OBSOLETE' | 'EN_ERREUR';
  demonstration: boolean;
  valeur: number | null;
  periodeDebut: string | null;
  periodeFin: string | null;
  actualiseLe: string | null;
  message: string | null;
  ventilations: Array<{ dimension: string; cle: string; libelle?: string; valeur: number }>;
}

export interface EtatSourceIndicateurs {
  source: 'CENTRALIZED' | 'REPARATIONS';
  libelle: string;
  derniereReception: string | null;
  indicateurs: number;
  aJour: number;
  obsoletes: number;
  enErreur: number;
  nonConnectes: number;
  demonstration: boolean;
}

export interface BlocResultats {
  code: 'ETUDES_JUSTICE' | 'REPARATIONS';
  libelle: string;
  source: EtatSourceIndicateurs;
  familles: Array<{ libelle: string; indicateurs: IndicateurResultat[] }>;
}

export interface TableauIndicateurs {
  blocs: BlocResultats[];
  genereLe: string;
}

export interface TableauDeBord {
  utilisateur: { nom: string; fonction: string; structureCode: string; profils: Array<{ code: Profil; libelle: string }>; perimetre: 'FONDS' | 'STRUCTURES'; structures: string[] };
  aTraiter: { total: number; tdr: number; fed: number; note: number; bc: number; req: number; enRetard: number; dossiers: DossierATraiter[] };
  documents: { tdr: Partial<Record<StatutDocument, number>>; fed: Partial<Record<StatutDocument, number>>; note: Partial<Record<StatutDocument, number>>; bc: Partial<Record<StatutDocument, number>>; req: Partial<Record<StatutDocument, number>>; autorisesCeMois: number; enRetard: number };
  delais: { tdrHeuresMoyennes: number | null; fedHeuresMoyennes: number | null; cible: number };
  budget: { exercice: number; total: TotauxBudget; structures: SyntheseStructure[] };
  tresorerie: { aPayer: number; montantAPayer: number; liquides: number; montantLiquide: number; payesCeMois: number; montantPayeCeMois: number } | null;
  ressources: SituationRessources | null;
  /** Blocs « Résultats » (Centralized, réparations) ; null pour les profils non concernés. */
  resultats: TableauIndicateurs | null;
  /** Démarches RH (spécifications v1.4, § 7.2) : solde de congé, prochaine absence, demandes en cours, congés à traiter, indicateurs RH. */
  rh: BlocRh;
  /** Documents émis par l'utilisateur : compteurs par statut, corrections attendues, dossiers en circuit (v1.1, § 9.5). */
  mesDocuments: {
    tdr: Record<string, number>;
    fed: Record<string, number>;
    req: Record<string, number>;
    /** Notes explicatives émises (GEDEM v3.0, § 7). */
    ne: Record<string, number>;
    corrections: Array<{ type: 'TDR' | 'FED' | 'REQ'; id: string; numero: string | null; objet: string; dateLimite: string; joursOuvrablesRestants: number; expiree: boolean }>;
    enCircuit: Array<{ type: 'TDR' | 'FED' | 'REQ' | 'NE'; id: string; numero: string | null; objet: string; montant: number; etape: Etape | null; depuis: string | null; heures: number }>;
  };
  /** Répartition des TDR et FED en validation par étape du circuit, avec montants. */
  niveaux: Array<{ etape: Etape; tdr: { nombre: number; montant: number }; fed: { nombre: number; montant: number } }>;
  /** Alertes consolidées (retards, corrections, instructions, factures, budget, trésorerie, indicateurs). */
  alertes: Array<{ code: string; gravite: 'critique' | 'alerte' | 'info'; libelle: string; detail: string; lien: string }>;
  /** Bloc « Plan opérationnel » composé selon le profil ; null hors module. */
  plan: BlocPlanTableau | null;
  recents: Array<{ type: TypeDossier; id: string; numero: string | null; objet: string; statut: StatutDocument; montant: number; structureCode: string | null; updatedAt: string }>;
  courrier: BlocCourrier;
}

// ------------------------------------------------------------------ notes explicatives
export type NatureChangement = 'DATES' | 'EQUIPES' | 'LIEUX' | 'ACTIVITES' | 'BUDGET';

export const LIBELLE_NATURE_CHANGEMENT: Record<NatureChangement, string> = {
  DATES: 'Dates',
  EQUIPES: 'Composition des équipes',
  LIEUX: 'Lieux',
  ACTIVITES: 'Activités et agenda',
  BUDGET: 'Budget',
};

export interface LigneBudgetNote {
  bloc: 'A' | 'B';
  nature: string;
  ancien: number;
  nouveau: number;
}

export interface NoteExplicative {
  id: string;
  numero: string | null;
  tdrId: string;
  structureId: string;
  structure?: Structure;
  objet: string;
  natures: NatureChangement[];
  motifs: string;
  impactBudgetaire: boolean;
  budget: LigneBudgetNote[] | null;
  montantAncien: number;
  montantNouveau: number;
  statut: StatutDocument;
  emetteurId: string;
  emetteur?: Agent;
  circuitInstanceId: string | null;
  dateSoumission: string | null;
  dateAutorisation: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TdrResumeNote {
  id: string;
  numero: string | null;
  titre: string;
  statut: StatutDocument;
  montantA: number;
  montantB: number;
  montant: number;
  ligneBudgetaire: LigneBudgetaireRef | null;
}

export interface FedLieNote {
  id: string;
  numero: string | null;
  statut: StatutDocument;
  montantUsd: number;
  suspendu: boolean;
}

export interface BaseNote {
  tdr: TdrResumeNote;
  budget: LigneBudgetNote[];
  feds: FedLieNote[];
  disponible: number;
}

export interface DetailNote {
  note: NoteExplicative;
  ecart: number;
  circuit: Circuit | null;
  etapeCourante: Etape | null;
  actions: Actions;
  controles: Controle[];
  tdr: TdrResumeNote;
  feds: FedLieNote[];
}

export interface NoteResume {
  id: string;
  numero: string | null;
  objet: string;
  natures: NatureChangement[];
  impactBudgetaire: boolean;
  montantAncien: number;
  montantNouveau: number;
  ecart: number;
  statut: StatutDocument;
  etapeCourante: Etape | null;
  structureCode: string | null;
  tdr: { id: string; numero: string | null; titre: string } | null;
  emetteur: { id: string; nom: string } | null;
  dateSoumission: string | null;
  dateAutorisation: string | null;
  updatedAt: string;
}

// ------------------------------------------------------------------ bons de commande
export type CategorieAchat = 'VEHICULES' | 'VOYAGES' | 'HEBERGEMENT' | 'FOURNITURES' | 'IMPRESSION' | 'INFORMATIQUE' | 'PRESTATIONS' | 'AUTRE';

export const LIBELLE_CATEGORIE_ACHAT: Record<CategorieAchat, string> = {
  VEHICULES: 'Véhicules et location',
  VOYAGES: 'Voyages et billets',
  HEBERGEMENT: 'Hébergement',
  FOURNITURES: 'Fournitures',
  IMPRESSION: 'Impression',
  INFORMATIQUE: 'Informatique',
  PRESTATIONS: 'Prestations',
  AUTRE: 'Autre',
};

export interface LigneCommande {
  designation: string;
  quantite: number;
  unite: string;
  prixUnitaire: number;
}

export interface Consultation {
  fournisseur: string;
  montant: number;
}

export interface BonCommande {
  id: string;
  numero: string | null;
  structureId: string;
  structure?: Structure;
  ligneBudgetaireId: string;
  ligneBudgetaire?: LigneBudgetaireRef;
  tdrId: string | null;
  tdr?: Tdr | null;
  requisition: string | null;
  /** Réquisition autorisée dont ce bon est la transformation. */
  requisitionId: string | null;
  objet: string;
  categorie: CategorieAchat;
  fournisseur: string;
  lignes: LigneCommande[];
  consultations: Consultation[];
  justification: string | null;
  delaiLivraison: string | null;
  lieuLivraison: string | null;
  montant: number;
  devise: string;
  montantUsd: number;
  statut: StatutDocument;
  emetteurId: string;
  emetteur?: Agent;
  circuitInstanceId: string | null;
  dateSoumission: string | null;
  dateAutorisation: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DetailBonCommande {
  bonCommande: BonCommande;
  circuit: Circuit | null;
  etapeCourante: Etape | null;
  actions: Actions & { facturer: boolean };
  controles: Controle[];
  budget: Verification | null;
  moinsDisant: Consultation | null;
  feds: Array<{ id: string; numero: string | null; statut: StatutDocument; montantUsd: number; etatPaiement: EtatPaiement }>;
  requisitionLiee: { id: string; numero: string | null; objet: string; montantEstime: number; emetteur: string | null } | null;
}

export interface BcResume {
  id: string;
  numero: string | null;
  objet: string;
  categorie: CategorieAchat;
  fournisseur: string;
  requisition: string | null;
  requisitionId: string | null;
  statut: StatutDocument;
  etapeCourante: Etape | null;
  montant: number;
  devise: string;
  montantUsd: number;
  structureCode: string | null;
  structureLibelle: string | null;
  ligneBudgetaire: LigneBudgetaireRef | null;
  emetteur: { id: string; nom: string } | null;
  dateSoumission: string | null;
  dateAutorisation: string | null;
  updatedAt: string;
}

// ------------------------------------------------------------------ réquisitions (v1.1, § 10)
export interface ArticleRequisition {
  designation: string;
  quantite: number;
  unite: string;
  estimationUsd: number;
}

export interface Requisition {
  id: string;
  numero: string | null;
  structureId: string;
  structure?: Structure;
  ligneBudgetaireId: string;
  ligneBudgetaire?: LigneBudgetaireRef;
  objet: string;
  justification: string;
  articles: ArticleRequisition[];
  montantEstime: number;
  delaiSouhaite: string | null;
  urgence: boolean;
  justificationUrgence: string | null;
  memorial: boolean;
  statut: StatutDocument;
  emetteurId: string;
  emetteur?: Agent;
  circuitInstanceId: string | null;
  dateSoumission: string | null;
  dateAutorisation: string | null;
  correctionDemandeeLe: string | null;
  motifAnnulation: string | null;
  annulationAutomatique: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DetailRequisition {
  requisition: Requisition;
  circuit: Circuit | null;
  etapeCourante: Etape | null;
  actions: Actions & { commander: boolean };
  controles: Controle[];
  budget: Verification | null;
  bonsCommande: Array<{ id: string; numero: string | null; statut: StatutDocument; fournisseur: string; montantUsd: number }>;
  correction: EcheanceCorrection | null;
}

export interface RequisitionResume {
  id: string;
  numero: string | null;
  objet: string;
  statut: StatutDocument;
  etapeCourante: Etape | null;
  montantEstime: number;
  articles: number;
  urgence: boolean;
  memorial: boolean;
  structureCode: string | null;
  structureLibelle: string | null;
  ligneBudgetaire: LigneBudgetaireRef | null;
  emetteur: { id: string; nom: string } | null;
  dateSoumission: string | null;
  dateAutorisation: string | null;
  updatedAt: string;
  correction: EcheanceCorrection | null;
  motifAnnulation: string | null;
  annulationAutomatique: boolean;
}

// ------------------------------------------------------------------ courrier et factures reçues
export type Priorite = 'NORMALE' | 'URGENTE' | 'TRES_URGENTE';
export const LIBELLE_PRIORITE: Record<Priorite, string> = { NORMALE: 'Normale', URGENTE: 'Urgente', TRES_URGENTE: 'Très urgente' };

export type TypeDocumentCourrier = 'LETTRE' | 'DEMANDE' | 'PLAINTE' | 'INVITATION' | 'RAPPORT' | 'NOTE' | 'CONVENTION' | 'MISE_EN_DEMEURE' | 'CIRCULAIRE' | 'AUTRE';
export const LIBELLE_TYPE_DOCUMENT: Record<TypeDocumentCourrier, string> = {
  LETTRE: 'Lettre',
  DEMANDE: 'Demande',
  PLAINTE: 'Plainte',
  INVITATION: 'Invitation',
  RAPPORT: 'Rapport',
  NOTE: 'Note',
  CONVENTION: 'Convention ou contrat',
  MISE_EN_DEMEURE: 'Mise en demeure',
  CIRCULAIRE: 'Circulaire',
  AUTRE: 'Autre',
};

export type TypeInstruction = 'ACTION' | 'AVIS' | 'INFORMATION' | 'REPONSE' | 'SUIVI';
export const LIBELLE_TYPE_INSTRUCTION: Record<TypeInstruction, string> = { ACTION: 'Pour action', AVIS: 'Pour avis', INFORMATION: 'Pour information', REPONSE: 'Préparer une réponse', SUIVI: 'Pour suivi' };

export type StatutCourrier = 'ENREGISTRE' | 'ANALYSE' | 'SOUMIS' | 'ANNOTE' | 'TRANSMIS' | 'EN_COURS' | 'REPONSE_RECUE' | 'CLOTURE' | 'ANNULE';
export const LIBELLE_STATUT_COURRIER: Record<StatutCourrier, string> = {
  ENREGISTRE: 'Enregistré',
  ANALYSE: 'En analyse',
  SOUMIS: 'Soumis au DG',
  ANNOTE: 'Annoté par le DG',
  TRANSMIS: 'Transmis',
  EN_COURS: 'En cours de traitement',
  REPONSE_RECUE: 'Réponse reçue',
  CLOTURE: 'Clôturé',
  ANNULE: 'Annulé',
};

export type StatutFacture = 'ENREGISTREE' | 'SOUMISE' | 'ORIENTEE' | 'SUSPENDUE' | 'RETOURNEE' | 'COMPTABILISEE' | 'TRANSMISE_CPM' | 'FED_EMIS' | 'MISE_EN_PAIEMENT' | 'PAYEE' | 'CLOTUREE' | 'ANNULEE';
export const LIBELLE_STATUT_FACTURE: Record<StatutFacture, string> = {
  ENREGISTREE: 'Enregistrée',
  SOUMISE: 'Soumise au DG',
  ORIENTEE: 'Orientée vers la Finance',
  SUSPENDUE: 'Suspendue',
  RETOURNEE: "Retournée à l'assistante",
  COMPTABILISEE: 'Comptabilisée',
  TRANSMISE_CPM: 'Transmise à la CPM',
  FED_EMIS: 'FED émis',
  MISE_EN_PAIEMENT: 'Mise en paiement',
  PAYEE: 'Payée',
  CLOTUREE: 'Clôturée',
  ANNULEE: 'Annulée',
};

export type StatutInstruction = 'NOUVELLE' | 'CONSULTEE' | 'EN_COURS' | 'REPONSE' | 'COMPLEMENT' | 'CLOTUREE';
export const LIBELLE_STATUT_INSTRUCTION: Record<StatutInstruction, string> = {
  NOUVELLE: 'Nouvelle (non consultée)',
  CONSULTEE: 'Consultée',
  EN_COURS: 'En cours',
  REPONSE: 'Réponse reçue · à valider',
  COMPLEMENT: 'Complément demandé',
  CLOTUREE: 'Clôturée',
};

export interface EvenementHistorique {
  date: string;
  auteurId: string;
  auteurNom: string;
  action: string;
  detail: string | null;
  titulaireId?: string | null;
}

export interface OrientationProposee {
  responsableId: string;
  type: TypeInstruction;
  texte: string;
  echeance: string | null;
}

export interface CourrierEntrant {
  id: string;
  numero: string;
  dateReception: string;
  dateCourrier: string | null;
  expediteur: string;
  referenceExpediteur: string | null;
  objet: string;
  typeDocument: TypeDocumentCourrier;
  priorite: Priorite;
  confidentiel: boolean;
  motsCles: string[] | null;
  document: string | null;
  conseillerId: string | null;
  conseiller?: Agent | null;
  analyse: { texte: string; avis: string | null; orientation: OrientationProposee[]; auteurId: string; date: string } | null;
  annotation: string | null;
  statut: StatutCourrier;
  motifCloture: string | null;
  enregistreParId: string;
  enregistrePar?: Agent;
  historique: EvenementHistorique[];
  dateTransmissionCt: string | null;
  dateAnalyse: string | null;
  dateSoumission: string | null;
  dateDecision: string | null;
  dateCloture: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CourrierResume {
  id: string;
  numero: string;
  dateReception: string;
  expediteur: string;
  objet: string;
  typeDocument: TypeDocumentCourrier;
  priorite: Priorite;
  confidentiel: boolean;
  statut: StatutCourrier;
  conseiller: { id: string; nom: string } | null;
  avecAnalyse: boolean;
  dateSoumission: string | null;
  dateDecision: string | null;
  dateCloture: string | null;
  updatedAt: string;
}

export interface InstructionResume {
  id: string;
  reference: string;
  type: TypeInstruction;
  texte: string;
  priorite: Priorite;
  echeance: string | null;
  statut: StatutInstruction;
  responsable: { id: string; nom: string; fonction: string } | null;
  structureCode: string | null;
  auteur: { id: string; nom: string } | null;
  titulaireId: string | null;
  document: { type: 'CE' | 'FA'; id: string; numero: string; objet: string } | null;
  reaffectations: number;
  demandeReaffectation: string | null;
  reponse: string | null;
  dateConsultation: string | null;
  dateReponse: string | null;
  dateCloture: string | null;
  createdAt: string;
  joursEcoules: number;
  joursRetard: number;
  enRetard: boolean;
  rappel: boolean;
  derniereAction: { action: string; date: string } | null;
}

export interface DetailCourrier {
  courrier: CourrierEntrant;
  instructions: InstructionResume[];
  doublons: Array<{ id: string; numero: string; objet: string; dateReception: string }>;
  delaiAnalyse: { jours: number; depasse: boolean } | null;
  actions: { modifier: boolean; transmettreAnalyse: boolean; analyser: boolean; soumettre: boolean; decider: boolean; cloturer: boolean };
}

export interface Instruction {
  id: string;
  reference: string;
  courrierId: string | null;
  courrier?: CourrierEntrant | null;
  factureId: string | null;
  facture?: FactureRecue | null;
  type: TypeInstruction;
  texte: string;
  priorite: Priorite;
  echeance: string | null;
  structureId: string;
  structure?: Structure;
  responsableId: string;
  responsable?: Agent;
  auteurId: string;
  auteur?: Agent;
  titulaireId: string | null;
  statut: StatutInstruction;
  reponse: string | null;
  demandeReaffectation: string | null;
  reaffectations: Array<{ date: string; auteurNom: string; ancienResponsableNom: string; nouveauResponsableNom: string; motif: string | null }>;
  historique: EvenementHistorique[];
  dateConsultation: string | null;
  dateReponse: string | null;
  dateCloture: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DetailInstruction {
  instruction: Instruction;
  etat: { joursEcoules: number; joursRetard: number; enRetard: boolean; rappel: boolean; derniereAction: { action: string; date: string } | null };
  escaladeA: { agentId: string; nom: string } | null;
  actions: { marquerEnCours: boolean; repondre: boolean; prisConnaissance: boolean; valider: boolean; demanderReaffectation: boolean };
}

export interface FactureRecue {
  id: string;
  numero: string;
  fournisseur: string;
  numeroFacture: string;
  dateFacture: string;
  dateReception: string;
  objet: string;
  montant: number;
  tva: number;
  devise: string;
  montantUsd: number;
  referenceCommande: string | null;
  bonCommandeId: string | null;
  bonCommande?: BonCommande | null;
  ecartCommande: number | null;
  document: string | null;
  annotation: string | null;
  motif: string | null;
  ligneBudgetaireId: string | null;
  ligneBudgetaire?: LigneBudgetaireRef | null;
  commentaireComptable: string | null;
  fedId: string | null;
  statut: StatutFacture;
  enregistreParId: string;
  enregistrePar?: Agent;
  historique: EvenementHistorique[];
  dateSoumission: string | null;
  dateOrientation: string | null;
  dateComptabilisation: string | null;
  datePaiement: string | null;
  dateCloture: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FactureResume {
  id: string;
  numero: string;
  fournisseur: string;
  numeroFacture: string;
  objet: string;
  montant: number;
  devise: string;
  montantUsd: number;
  dateReception: string;
  statut: StatutFacture;
  bonCommande: { id: string; numero: string | null } | null;
  referenceCommande: string | null;
  ecartCommande: number | null;
  jours: number;
  procheDelai: boolean;
  delaiDepasse: boolean;
  updatedAt: string;
}

export interface DetailFacture {
  facture: FactureRecue;
  instructions: InstructionResume[];
  fed: { id: string; numero: string | null; statut: StatutDocument; etatPaiement: EtatPaiement; montantUsd: number } | null;
  delai: { jours: number; cible: number; proche: boolean; depasse: boolean };
  actions: { modifier: boolean; soumettre: boolean; decider: boolean; comptabiliser: boolean; retourner: boolean; engager: boolean; cloturer: boolean };
}

export interface Parapheur {
  parDelegation: { titulaireId: string } | null;
  compteurs: { courriers: number; factures: number; reponses: number; reaffectations: number; total: number };
  courriers: CourrierResume[];
  factures: FactureResume[];
  reponses: InstructionResume[];
  reaffectations: InstructionResume[];
}

export interface Delegation {
  id: string;
  titulaireId: string;
  titulaire?: Agent;
  delegataireId: string;
  delegataire?: Agent;
  debut: string;
  fin: string;
  portee: { courriers: boolean; factures: boolean; reponses: boolean; reaffectation: boolean; confidentiels: boolean };
  exclusions: string | null;
  actif: boolean;
  createdAt: string;
}

export interface Destinataire {
  agentId: string;
  nom: string;
  fonction: string;
  structureCode: string | null;
  profils: Profil[];
}

export interface BlocCourrier {
  aAnalyser: number;
  parapheur: { courriers: number; factures: number; reponses: number; reaffectations: number } | null;
  instructions: { perimetre: 'MIENNES' | 'TOUTES'; nouvelles: number; enCours: number; enRetard: number; aValider: number };
  factures: { enCircuit: number; prochesDelai: number } | null;
  enregistrement: { enAnalyse: number; soumis: number; transmis: number } | null;
}

export interface ResumeImportCourrier {
  date: string;
  boite: string;
  lus: number;
  crees: number;
  dejaImportes: number;
  erreurs: Array<{ message: string; motif: string }>;
  courriers: Array<{ id: string; numero: string; objet: string }>;
}
export interface EtatImportCourrier {
  configure: boolean;
  boite: string | null;
  dossier: string;
  dossierTraite: string | null;
  automatique: boolean;
  intervalleMinutes: number;
  dernierImport: ResumeImportCourrier | null;
}

export interface ServiceDirection {
  id: string;
  structureId: string;
  structureCode: string;
  structureLibelle: string;
  code: string;
  perimetre: string;
  libelle: string;
  chefs: ChefService[];
  responsableNom: string | null;
  responsableEmail: string | null;
  actif: boolean;
  lignes: Array<{ id: string; code: string; libelle: string; budgetActualise: number }>;
  updatedAt: string;
}
export interface ChefService {
  habilitationId: string;
  agentId: string;
  nom: string;
  email: string;
  titulaireId: string | null;
  titulaireNom: string | null;
  debut: string | null;
  fin: string | null;
  enCours: boolean;
}
export interface FiltresRapport {
  exercice: number;
  type: 'CUMUL' | 'MOIS' | 'TRIMESTRE';
  mois: number | null;
  trimestre: number | null;
  structureCode: string | null;
  serviceCode?: string | null;
  comparaison: 'AUCUNE' | 'N-1' | 'T-1';
}
export interface ResumeRapport {
  id: string;
  numero: string;
  type: string;
  perimetre: string;
  libellePerimetre: string;
  periode: string;
  filtres: FiltresRapport;
  nomFichier: string;
  taille: number;
  pages: number;
  nomFichierExcel: string | null;
  tailleExcel: number | null;
  synthese: Record<string, number | null>;
  automatique: boolean;
  etat: 'BROUILLON' | 'A_VISER' | 'A_VALIDER' | 'DEFINITIF';
  viseParNom: string | null;
  viseLe: string | null;
  valideParNom: string | null;
  valideLe: string | null;
  genereParNom: string;
  envoyeLe: string | null;
  nombreEnvois: number;
  createdAt: string;
}
export type StatutCommentaireCloture = 'SAISI' | 'VALIDE' | 'RETOURNE';
export const LIBELLE_STATUT_COMMENTAIRE: Record<StatutCommentaireCloture, string> = { SAISI: 'En attente de validation du DF', VALIDE: 'Validé par le DF', RETOURNE: 'Retourné par le DF' };
export const LIBELLE_ALERTE_RAPPORT: Record<string, string> = { DEPASSEMENT: 'Dépassement', RETARD: 'Retard', AVANCE: 'Avance', NON_PAYE_ANCIEN: 'Engagé non payé ancien' };
export type TypePeriodeCommentaire = 'MOIS' | 'TRIMESTRE' | 'ANNEE';
export interface CommentaireCloture {
  id: string;
  exercice: number;
  type: TypePeriodeCommentaire;
  indice: number;
  structureCode: string;
  serviceCode: string;
  texte: string;
  statut: StatutCommentaireCloture;
  auteurNom: string;
  saisiLe: string;
  valideParNom: string | null;
  valideLe: string | null;
  motifRetour: string | null;
  updatedAt: string;
}
export interface AttenteCommentaire {
  structureCode: string;
  structureLibelle: string;
  serviceCode: string;
  serviceLibelle: string;
  dateLimite: string;
  enRetard: boolean;
  nombreLignesEnAlerte: number;
  alertes: Array<{ code: string; detail: string }>;
  commentaire: CommentaireCloture | null;
  peutSaisir: boolean;
  peutValider: boolean;
}
export interface ClotureResume {
  periode: { exercice: number; type: TypePeriodeCommentaire; indice: number; libelle: string; debut: string; fin: string; dateLimite: string; enRetard: boolean };
  dateLimiteServices: string;
  attentes: AttenteCommentaire[];
}
export interface DestinataireEnvoi {
  nom: string;
  email: string;
  copie?: boolean;
}
export interface DestinataireRapport extends DestinataireEnvoi {
  id: string;
  perimetre: string;
  agentId: string | null;
  creeParNom: string;
  createdAt: string;
}
export interface DestinataireDefaut extends DestinataireEnvoi {
  agentId: string;
  motif: string;
}
export interface ListeDiffusion {
  perimetre: string;
  libelle: string;
  destinataires: DestinataireRapport[];
  parDefaut: DestinataireDefaut[];
}
export interface DestinatairesEffectifs {
  perimetre: string;
  listeDefinie: boolean;
  destinataires: DestinataireEnvoi[];
}
export type StatutEnvoiRapport = 'ENVOYE' | 'JOURNALISE' | 'ECHEC';
export interface EnvoiRapport {
  id: string;
  rapportId: string;
  sujet: string;
  destinataires: DestinataireEnvoi[];
  transport: string;
  statut: StatutEnvoiRapport;
  detail: string | null;
  pieceJointe: boolean;
  automatique: boolean;
  envoyeParNom: string;
  createdAt: string;
}
export const LIBELLE_STATUT_ENVOI: Record<StatutEnvoiRapport, string> = { ENVOYE: 'envoyé', JOURNALISE: 'journalisé (aucun transport)', ECHEC: 'échec' };
export interface LignePlanMensuel {
  id: string;
  code: string;
  libelle: string;
  structureCode: string;
  structureLibelle: string;
  programme: string;
  budgetRevise: number;
  plan: number[];
  saisi: boolean;
  coherent: boolean;
  somme: number;
  ecart: number;
  modifieLe: string | null;
  modifiePar: string | null;
}
export interface PlanMensuel {
  exercice: number;
  lignes: LignePlanMensuel[];
  totaux: { budgetRevise: number; plan: number[]; saisies: number; incoherentes: number };
}
export interface ResumeImportPlan {
  exercice: number;
  lignesLues: number;
  enregistrees: number;
  inchangees: number;
  erreurs: Array<{ code: string; message: string }>;
}
export interface PerimetresRapport {
  fonds: boolean;
  structures: Array<{ code: string; libelle: string }>;
  services: Array<{ code: string; libelle: string; structureCode: string; structureLibelle: string; perimetre: string }>;
}

// ------------------------------------------------------------------ pièces jointes et notifications
export type ObjetPiece = 'TDR' | 'FED' | 'NE' | 'BC' | 'REQ' | 'CE' | 'FA' | 'IN' | 'PLAN' | 'CONGE' | 'PRET' | 'PROCEDURE' | 'AGENT';
export type TypePiece = 'DOCUMENT_PRINCIPAL' | 'PROFORMA' | 'FACTURE' | 'BON_COMMANDE' | 'CONTRAT' | 'LISTE_PRESENCE' | 'RAPPORT' | 'COURRIER' | 'JUSTIFICATIF' | 'AVIS_DEBIT' | 'RECU_CAISSE' | 'DOCUMENT_SIGNE' | 'PHOTO' | 'PIECE_IDENTITE' | 'ACTE_ETAT_CIVIL' | 'AUTRE';
export const LIBELLE_TYPE_PIECE: Record<TypePiece, string> = {
  DOCUMENT_PRINCIPAL: 'Document principal numérisé',
  PROFORMA: 'Proforma',
  FACTURE: 'Facture',
  BON_COMMANDE: 'Bon de commande',
  CONTRAT: 'Contrat',
  LISTE_PRESENCE: 'Liste de présence',
  RAPPORT: 'Rapport',
  COURRIER: 'Courrier',
  JUSTIFICATIF: 'Justificatif',
  AVIS_DEBIT: 'Avis de débit bancaire',
  RECU_CAISSE: 'Reçu de caisse signé',
  DOCUMENT_SIGNE: 'Version signée (PDF)',
  PHOTO: 'Photo de l’agent',
  PIECE_IDENTITE: 'Pièce d’identité',
  ACTE_ETAT_CIVIL: 'Acte d’état civil',
  AUTRE: 'Autre',
};

export interface PieceJointe {
  id: string;
  objetType: ObjetPiece;
  objetId: string;
  nomFichier: string;
  typeMime: string;
  taille: number;
  sha256: string;
  type: TypePiece;
  commentaire: string | null;
  apresSoumission: boolean;
  version: number;
  remplaceParId: string | null;
  remplacee: boolean;
  deposePar: { id: string; nom: string } | null;
  createdAt: string;
}

export interface ListePieces {
  pieces: PieceJointe[];
  peutJoindre: boolean;
  apresSoumission: boolean;
}

export interface Notification {
  id: string;
  code: string;
  titre: string;
  message: string;
  lien: string | null;
  objetType: string | null;
  objetId: string | null;
  priorite: 'NORMALE' | 'URGENTE';
  lue: boolean;
  dateLecture: string | null;
  createdAt: string;
}

export interface PreferencesNotification {
  courriel: boolean;
  recapitulatif: boolean;
  modeCourriel: 'graph' | 'smtp' | 'journal';
}

export interface ResumeRelances {
  date: string;
  instructions: number;
  alertesConsultation: number;
  rappels: number;
  retards: number;
  relances: number;
  escalades: number;
  alertesFactures: number;
}

// ------------------------------------------------------------------ administration
export const PROFILS: Profil[] = ['EMETTEUR', 'ASSISTANT', 'CONSEILLER_TECHNIQUE', 'DIRECTEUR', 'CHEF_SERVICE', 'COORDONNATEUR_MEMORIAL', 'CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_OPERATIONS', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'TRESORERIE', 'TRESORIER', 'CAISSIER', 'CHEF_COMPTABLE', 'CHARGE_ACHATS', 'RESPONSABLE_ACHATS', 'CELLULE_MARCHES', 'DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'INVESTISSEMENTS', 'PAIE', 'ADMINISTRATEUR'];
export const LIBELLE_PROFIL: Record<Profil, string> = {
  EMETTEUR: 'Émetteur (agent)',
  ASSISTANT: 'Assistant de direction',
  CONSEILLER_TECHNIQUE: 'Conseiller technique',
  DIRECTEUR: 'Directeur',
  CHEF_SERVICE: 'Chef de service',
  COORDONNATEUR_MEMORIAL: 'Coordonnateur du Mémorial',
  CONTROLE_GESTION: 'Contrôle de gestion',
  DIRECTEUR_FINANCIER: 'Directeur financier',
  DGA_OPERATIONS: 'DGA chargé des opérations',
  DGA_FINANCES: 'DGA Administration et Finances',
  DIRECTEUR_GENERAL: 'Directeur Général',
  TRESORERIE: 'Responsable de la trésorerie',
  TRESORIER: 'Trésorier',
  CAISSIER: 'Caissier',
  CHEF_COMPTABLE: 'Chef comptable',
  CHARGE_ACHATS: 'Chargé des achats',
  RESPONSABLE_ACHATS: 'Responsable des achats',
  CELLULE_MARCHES: 'Cellule de passation des marchés',
  DIRECTEUR_RH: 'Directeur RH et Juridique',
  RESSOURCES_HUMAINES: 'Gestionnaire des ressources humaines',
  INVESTISSEMENTS: 'Division des investissements',
  PAIE: 'Gestionnaire de la paie',
  ADMINISTRATEUR: 'Administrateur fonctionnel et technique',
};
/** Profils dont le périmètre est toujours tout le Fonds. */
export const PROFILS_TRANSVERSAUX: Profil[] = ['CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_FINANCES', 'DIRECTEUR_GENERAL', 'TRESORERIE', 'TRESORIER', 'CAISSIER', 'CHEF_COMPTABLE', 'RESPONSABLE_ACHATS', 'CELLULE_MARCHES', 'DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'INVESTISSEMENTS', 'PAIE', 'ADMINISTRATEUR'];

export const CATEGORIES_AGENT = ['PCA', 'DG', 'DGA', 'ADM', 'CAC', 'DIR', 'SDIR', 'CDIV', 'CSERV', 'CBUR', 'ACOL', 'AEXE'] as const;
export type CategorieAgent = (typeof CATEGORIES_AGENT)[number];
export const LIBELLE_CATEGORIE: Record<CategorieAgent, string> = {
  PCA: 'Président du Conseil d’administration',
  DG: 'Directeur Général',
  DGA: 'Directeur Général Adjoint',
  ADM: 'Administrateur',
  CAC: 'Commissaire aux comptes',
  DIR: 'Directeur',
  SDIR: 'Sous-directeur',
  CDIV: 'Chef de division',
  CSERV: 'Chef de service',
  CBUR: 'Chef de bureau',
  ACOL: 'Agent de collaboration',
  AEXE: 'Agent d’exécution',
};

export interface HabilitationAdmin {
  id: string;
  profil: Profil;
  libelle: string;
  structure: { id: string; code: string; libelle: string } | null;
  service: { id: string; code: string; libelle: string } | null;
  titulaire: { id: string; nom: string } | null;
  debut: string | null;
  fin: string | null;
  actif: boolean;
}

export interface Utilisateur {
  id: string;
  matricule: string;
  nom: string;
  prenom: string;
  email: string;
  fonction: string;
  structure: { id: string; code: string; libelle: string } | null;
  categorie: CategorieAgent;
  base: 'SIEGE' | 'COORDINATION';
  province: string | null;
  actif: boolean;
  microsoft: boolean;
  compteLocal: { actif: boolean; totp: boolean; doitChangerMotDePasse: boolean; verrouille: boolean; dernierAcces: string | null } | null;
  habilitations: HabilitationAdmin[];
  profils: Profil[];
  /** Champs RH tenus par les Ressources humaines (démarches RH, § 3.4). */
  rh: { grade: string | null; superieur: { id: string; nom: string } | null; dateEngagement: string | null; dateAnciennete: string | null; regimeConge: RegimeConge | null; regimeConfirmeLe: string | null; eligibleRh: boolean };
}

export type ActionCompte = 'CREER' | 'REINITIALISER_MOT_DE_PASSE' | 'REINITIALISER_TOTP' | 'DEVERROUILLER' | 'DESACTIVER' | 'ACTIVER';

export type GroupeParametre = 'SECURITE' | 'CIRCUIT' | 'PILOTAGE' | 'COURRIER' | 'NOTIFICATIONS' | 'TRESORERIE' | 'RAPPORTS' | 'RESSOURCES' | 'PTBA' | 'CONGES' | 'PRETS' | 'PROCEDURES' | 'HRMS' | 'PAIE';
export const LIBELLE_GROUPE_PARAMETRE: Record<GroupeParametre, string> = { SECURITE: 'Sécurité et connexion', CIRCUIT: 'Circuit de validation', PILOTAGE: 'Tableaux de bord', COURRIER: 'Courrier et factures', NOTIFICATIONS: 'Notifications', TRESORERIE: 'Trésorerie', RAPPORTS: 'Rapports d’exécution', RESSOURCES: 'Ressources et quotes-parts', PTBA: 'Plan de travail et budget annuel (PTBA)', CONGES: 'Congés (démarches RH)', PRETS: 'Prêts sociaux', PROCEDURES: 'Bibliothèque des procédures', HRMS: 'Référentiel HRMS', PAIE: 'Paie : barème et rémunérations' };
export interface ParametreApp {
  cle: string;
  groupe: GroupeParametre;
  libelle: string;
  description: string;
  type: 'nombre' | 'booleen' | 'liste' | 'delais' | 'texte';
  defaut: unknown;
  min?: number;
  max?: number;
  valeur: unknown;
  parDefaut: boolean;
  modifiePar: string | null;
  modifieLe: string | null;
}
export type DelaisCourrier = Record<Priorite, { echeance: number; analyse: number; rappel: number; escalade: number }>;

export type StatutBareme = 'EN_PREPARATION' | 'EN_VIGUEUR' | 'ARCHIVE';
export const LIBELLE_STATUT_BAREME: Record<StatutBareme, string> = { EN_PREPARATION: 'En préparation', EN_VIGUEUR: 'En vigueur', ARCHIVE: 'Archivé' };
export interface VersionBareme {
  version: string;
  libelle: string;
  source: string;
  debut: string;
  fin: string | null;
  statut: StatutBareme;
}
export interface TauxPersonnel {
  pd: number;
  lg: number;
  rp: number;
  pr: number;
  tr: number;
}
export interface BaremeContenu {
  version: string;
  libelle: string;
  source: string;
  debut: string;
  fin: string | null;
  personnel: Record<string, { libelle: string; int: TauxPersonnel; ext: TauxPersonnel }>;
  coordinations: string[];
  interprovincial: Record<string, { hb: number; pd: number; tr: number }>;
  intraprovincial: Record<string, { pd: number; tr: number }>;
  kinshasaIntra: Record<string, number>;
  villes: Array<{ coordination: string; province: string; ville: string; hebergement: number }>;
  fraisTerminaux: { gopassINT: number; gopassEXT: number };
  tauxChange: { EUR: number };
}
export interface BaremeComplet extends VersionBareme {
  contenu: BaremeContenu;
}

export interface EvenementAudit {
  id: string;
  seq: string;
  horodatage: string;
  utilisateurId: string;
  utilisateurNom: string | null;
  titulaireId: string | null;
  titulaireNom: string | null;
  action: string;
  objetType: string;
  objetId: string;
  detail: Record<string, unknown> | null;
  adresseIp: string | null;
  appareil: string | null;
}
export interface RechercheAudit {
  total: number;
  page: number;
  limite: number;
  evenements: EvenementAudit[];
}
export interface VerificationAudit {
  ok: boolean;
  nombre: number;
  premierEcart: string | null;
  ancreCoherente: boolean;
}


// ------------------------------------------------------------------ trésorerie (spécification v1.5)
export type StatutDossier = 'VALIDE' | 'CONTROLE' | 'RETOURNE' | 'TRANSMIS' | 'PREPARATION' | 'POSTE' | 'A_CONFIRMER' | 'PAYE' | 'RAPPROCHE' | 'ANNULE' | 'SUSPENDU';
export const STATUTS_DOSSIER: StatutDossier[] = ['VALIDE', 'CONTROLE', 'RETOURNE', 'TRANSMIS', 'PREPARATION', 'POSTE', 'A_CONFIRMER', 'PAYE', 'RAPPROCHE', 'ANNULE', 'SUSPENDU'];
export const LIBELLE_STATUT_DOSSIER: Record<StatutDossier, string> = {
  VALIDE: 'Validé, reçu en trésorerie',
  CONTROLE: 'En contrôle trésorerie',
  RETOURNE: "Retourné à l'émetteur",
  TRANSMIS: 'Transmis, inscrit au plan',
  PREPARATION: 'En préparation du paiement',
  POSTE: 'Posté en banque',
  A_CONFIRMER: 'Validé en banque, à confirmer',
  PAYE: 'Payé',
  RAPPROCHE: 'Payé et rapproché',
  ANNULE: 'Annulé',
  SUSPENDU: 'Suspendu (note explicative)',
};
export type CleChecklist = 'circuit' | 'certification' | 'pieces' | 'montants' | 'beneficiaire' | 'doublon' | 'decaissement';
export type CategorieRetour = 'PIECES_MANQUANTES' | 'MONTANT_INCOHERENT' | 'BENEFICIAIRE_NON_IDENTIFIE' | 'IMPUTATION_ERRONEE' | 'DOUBLON' | 'AUTRE';
export const LIBELLE_CATEGORIE_RETOUR: Record<CategorieRetour, string> = {
  PIECES_MANQUANTES: 'Pièces justificatives manquantes',
  MONTANT_INCOHERENT: 'Montant incohérent',
  BENEFICIAIRE_NON_IDENTIFIE: 'Bénéficiaire non identifié',
  IMPUTATION_ERRONEE: 'Imputation budgétaire erronée',
  DOUBLON: 'Doublon',
  AUTRE: 'Autre',
};
export type ModePaiement = 'VIREMENT' | 'ESPECES';
export type Validateur = 'DF' | 'DGA' | 'DG';
export const LIBELLE_VALIDATEUR: Record<Validateur, string> = { DF: 'Directeur financier', DGA: 'DGA Finances', DG: 'Directeur Général' };

export interface SoldesCompte {
  soldeReleve: number;
  dateReleve: string | null;
  payesNonAuReleve: number;
  soldeDisponible: number;
  postesEnAttente: number;
  programmesSemaine: number;
  soldePrevisionnel: number;
  soldeMinimum: number;
  sousMinimum: boolean;
}
export interface ResumeCompte extends SoldesCompte {
  id: string;
  code: string;
  type: 'BANQUE' | 'CAISSE';
  banque: string;
  intitule: string;
  numero: string | null;
  devise: string;
  plafondOperation: number | null;
  soldeMaximum: number | null;
  actif: boolean;
  tresoriers: Array<{ affectationId: string; agentId: string; nom: string; suppleant: boolean }>;
}

export interface DrapeauxDossier {
  horsBudget: boolean;
  justificationHorsBudget: string | null;
  regularisation: boolean;
  doublonPossible: { avec: string; numero: string | null; montant: number } | null;
  doublonConfirme: boolean;
  revalideApresNote: boolean;
}
export interface ReportPlan {
  date: string;
  de: string;
  vers: string;
  motif: string;
  auteurId: string;
  auteurNom: string;
  parDf?: boolean;
}
export interface PaiementVirement {
  compteBeneficiaire: { banque: string; numero: string; intitule: string; swift: string | null } | null;
  referenceFacture: string | null;
  libelle: string | null;
  numeroOrdre: string | null;
  dateSaisie: string | null;
  dateValeur: string | null;
  referenceDebit: string | null;
  pieceId: string | null;
  rejets: Array<{ date: string; motif: string }>;
}
export interface PaiementEspeces {
  numeroBon: string | null;
  beneficiaire: { nom: string; typePiece: string; numeroPiece: string } | null;
  pieceId: string | null;
}
export interface ValidationBancaire {
  validateur: Validateur;
  dateHeure: string;
  enregistreeLe: string;
  parId: string;
  parNom: string;
}
export interface ResumeDossier {
  id: string;
  statut: StatutDossier;
  libelleStatut: string;
  semaine: string;
  semaineInitiale: string;
  dateProposee: string | null;
  mode: ModePaiement | null;
  compte: { id: string; code: string; banque: string; type: 'BANQUE' | 'CAISSE' } | null;
  tresorier: { id: string; nom: string } | null;
  nbReports: number;
  drapeaux: DrapeauxDossier;
  validationsRequises: Validateur[];
  nbValidations: number;
  delai: { joursOuvres: number; cible: number | null; niveau: 'ok' | 'orange' | 'rouge' };
  fed: {
    id: string;
    numero: string | null;
    objet: string;
    montantUsd: number;
    nature: string;
    urgence: boolean;
    etatPaiement: string;
    structureCode: string | null;
    emetteur: { id: string; nom: string } | null;
    beneficiaire: string | null;
    ligneBudgetaire: { code: string; libelle: string } | null;
  };
  dateValidation: string;
  dateStatut: string;
  datePaiement: string | null;
}
export interface ActionsDossier {
  prendreEnCharge: boolean;
  retourner: boolean;
  reprendre: boolean;
  transmettre: boolean;
  reporter: boolean;
  preparer: boolean;
  renvoyer: boolean;
  poster: boolean;
  enregistrerValidation: boolean;
  rejetBanque: boolean;
  confirmer: boolean;
  payerEspeces: boolean;
  rapprocher: boolean;
  demanderAnnulation: boolean;
  approuverAnnulation: boolean;
}
export interface DossierComplet extends ResumeDossier {
  reports: ReportPlan[];
  checklist: Partial<Record<CleChecklist, boolean>>;
  categorieRetour: CategorieRetour | null;
  motifRetour: string | null;
  paiement: PaiementVirement | null;
  especes: PaiementEspeces | null;
  validations: ValidationBancaire[];
  rapprochement: { ligneReleve: string; date: string; parId: string } | null;
  annulation: { motif: string; demandeParId: string; demandeParNom: string; date: string; approuveeParId: string | null; dateApprobation: string | null } | null;
  historique: EvenementHistorique[];
  dateTransmission: string | null;
  datePoste: string | null;
  statutAvantSuspension: StatutDossier | null;
}
export interface VerificationDisponibilite {
  ligneId: string;
  code: string;
  montant: number;
  disponible: number;
  disponiblePrevisionnel: number;
  ok: boolean;
  previsionnelOk: boolean;
  depassement: number;
  message: string;
}
export interface DetailDossier {
  dossier: DossierComplet;
  lignes: Array<{ ordre: number; description: string; beneficiaireType: string; beneficiaire: string; montant: number }>;
  circuit: Circuit | null;
  budget: VerificationDisponibilite | null;
  compte: ResumeCompte | null;
  pieces: Array<{ id: string; nomFichier: string; type: TypePiece; createdAt: string }>;
  checklistLibelles: Array<{ cle: CleChecklist; libelle: string }>;
  actions: ActionsDossier;
}

export type EtatLignePlan = 'PROPOSE' | 'INSCRIT' | 'REPORTE' | 'PAYE' | 'SUSPENDU';
export const LIBELLE_ETAT_PLAN: Record<EtatLignePlan, string> = { PROPOSE: 'Proposé', INSCRIT: 'Inscrit', REPORTE: 'Reporté', PAYE: 'Payé', SUSPENDU: 'Suspendu' };
export interface LignePlan extends ResumeDossier {
  etat: EtatLignePlan;
  report: { vers: string; motif: string; parDf: boolean } | null;
}
export interface BlocPlan {
  compte: ResumeCompte | null;
  lignes: LignePlan[];
  programme: number;
  paye: number;
  soldeDisponible: number | null;
  soldePrevisionnel: number | null;
  couverture: number | null;
  depassement: boolean;
}
export interface PlanHebdomadaire {
  semaine: string;
  lundi: string;
  vendredi: string;
  enveloppe: number | null;
  arrete: boolean;
  dateArret: string | null;
  viseDf: boolean;
  dateVisa: string | null;
  modificationsApresArret: Array<{ date: string; dossierId: string; numero: string | null; action: string; auteurNom: string }>;
  blocs: BlocPlan[];
  totaux: { proposes: number; inscrits: number; payes: number; reportes: number; suspendus: number; programme: number };
  charge: number | null;
  seuilCharge: number;
  alerteCharge: boolean;
}

export interface LigneSituation {
  compteId: string;
  code: string;
  banque: string;
  intitule: string;
  type: 'BANQUE' | 'CAISSE';
  soldeReleve: number;
  dateReleve: string | null;
  payesNonAuReleve: number;
  soldeDisponible: number;
  postesEnAttente: number;
  programmesSemaine: number;
  soldePrevisionnel: number;
  soldeMinimum: number;
  sousMinimum: boolean;
}
export interface ContenuSituation {
  date: string;
  semaine: string;
  lignes: LigneSituation[];
  totalDisponible: number;
  totalPrevisionnel: number;
  decaissementsDuJour: number;
  decaissementsSemaine: number;
  enAttenteValidation: number;
  alertes: string[];
}
export interface SituationTransmise {
  id: string;
  date: string;
  contenu: ContenuSituation;
  transmiseParId: string;
  transmiseParNom: string;
  createdAt: string;
}
export type SituationDuJour = ContenuSituation & { transmise: SituationTransmise | null };

export interface ArreteCaisse {
  id: string;
  date: string;
  billetage: Record<string, number>;
  soldeTheorique: number;
  soldePhysique: number;
  ecart: number;
  commentaire: string | null;
  caissierId: string;
  caissierNom: string;
  contresigneParId: string | null;
  contresigneParNom: string | null;
  dateContresignature: string | null;
  createdAt: string;
}
export interface VueCaisse {
  caisse: ResumeCompte | null;
  paiements: { aPreparer: ResumeDossier[]; enPreparation: ResumeDossier[]; payesRecents: ResumeDossier[] };
  arreteDuJour: ArreteCaisse | null;
  alertes: string[];
}

export type TypeOperation = 'RETOUR_CAISSE' | 'NIVELLEMENT';
export const LIBELLE_TYPE_OPERATION: Record<TypeOperation, string> = { RETOUR_CAISSE: 'Retour de caisse', NIVELLEMENT: 'Nivellement' };
export type StatutOperation = 'A_VISER' | 'A_VALIDER_DF' | 'A_APPROUVER_DGA' | 'A_AUTORISER_DG' | 'VALIDEE' | 'EXECUTEE' | 'COMPTABILISEE' | 'REJETEE';
export interface DetailsRetourCaisse {
  deposant: string;
  dateRetour: string;
  lieu: 'CAISSE' | 'BANQUE';
  numeroRecu: string;
}
export interface DetailsNivellement {
  dateSouhaitee: string | null;
  dateExecution: string | null;
  referenceExecution: string | null;
}
export interface ResumeOperation {
  id: string;
  numero: string;
  type: TypeOperation;
  statut: StatutOperation;
  libelleStatut: string;
  montant: number;
  motif: string;
  emetteur: { id: string; nom: string } | null;
  fedOrigine: { id: string; numero: string | null; objet: string; montantUsd: number } | null;
  compteDebite: { id: string; code: string; banque: string } | null;
  compteCredite: { id: string; code: string; banque: string; type: 'BANQUE' | 'CAISSE' } | null;
  details: DetailsRetourCaisse | DetailsNivellement;
  historique: EvenementHistorique[];
  createdAt: string;
  actions: { viser: boolean; decider: boolean; executer: boolean };
}

export interface AccueilResponsable {
  role: 'RESPONSABLE';
  compteurs: Partial<Record<StatutDossier, number>>;
  totalDisponible: number;
  totalPrevisionnel: number;
  decaissementsSemaine: number;
  payesCetteSemaine: { nombre: number; montant: number };
  alertes: Array<{ niveau: 'info' | 'alerte' | 'critique'; message: string }>;
  situationDuJourTransmise: boolean;
  operationsEnAttente: number;
  comptes: ResumeCompte[];
  semaine: string;
}
export interface AccueilTresorier {
  role: 'TRESORIER';
  semaine: string;
  comptes: Array<{ compte: ResumeCompte; aPreparer: ResumeDossier[]; enAttenteValidateurs: ResumeDossier[]; aConfirmer: ResumeDossier[]; payesRecents: ResumeDossier[] }>;
  operationsAExecuter: number;
}
export interface AccueilCaissier extends VueCaisse {
  role: 'CAISSIER';
}
export type AccueilTresorerie = AccueilResponsable | AccueilTresorier | AccueilCaissier;

export interface EngagementPaye {
  id: string;
  datePaiement: string | null;
  numero: string | null;
  objet: string;
  beneficiaire: string | null;
  structureCode: string | null;
  compte: string | null;
  mode: ModePaiement | null;
  montant: number;
  reference: string | null;
  statut: StatutDossier;
}
export interface EngagementsNonPayes {
  total: number;
  montant: number;
  anciennete: Record<'0-7' | '8-30' | '31+', { nombre: number; montant: number }>;
  dossiers: ResumeDossier[];
}

// ------------------------------------------------------------------ ressources et quotes-parts
export type BeneficiaireQuotePart = 'CIA_VAR' | 'ECOSYSTEME';
export const LIBELLE_BENEFICIAIRE: Record<BeneficiaireQuotePart, string> = { CIA_VAR: 'CIA-VAR', ECOSYSTEME: 'Écosystème' };
export interface QuotePartSituation {
  due: number;
  payee: number;
  restantDu: number;
}
export interface TrimestreRessources {
  trimestre: number;
  libelle: string;
  debut: string;
  fin: string;
  cloture: boolean;
  prevu: number;
  encaisse: number;
  tauxRealisation: number | null;
  quotesParts: Record<BeneficiaireQuotePart, QuotePartSituation>;
  alerte: boolean;
}
export interface SituationRessources {
  exercice: number;
  dateSituation: string;
  taux: Record<BeneficiaireQuotePart, number>;
  trimestres: TrimestreRessources[];
  parSource: Array<{ source: string; prevu: number; encaisse: number; tauxRealisation: number | null }>;
  totaux: { prevu: number; encaisse: number; tauxRealisation: number | null; payeCumul: number; tauxCouverture: number | null; encaissements: number };
  quotesParts: Record<BeneficiaireQuotePart, QuotePartSituation & { taux: number; libelle: string }>;
  alertes: Array<{ code: 'QUOTE_PART_IMPAYEE'; beneficiaire: BeneficiaireQuotePart; trimestre: number; restantDu: number; detail: string }>;
}
export interface Encaissement {
  id: string;
  date: string;
  montant: number;
  source: string;
  reference: string | null;
  commentaire: string | null;
  annule: boolean;
  motifAnnulation: string | null;
  saisiParNom: string;
  createdAt: string;
  /** SAISIE à l'écran ou INTEGRATION (application de collecte des revenus). */
  origine: 'SAISIE' | 'INTEGRATION';
  referenceExterne: string | null;
}
export interface PrevisionRessource {
  id: string;
  trimestre: number;
  source: string;
  montant: number;
  modifieParNom: string;
  updatedAt: string;
}
export interface PaiementQuotePart {
  id: string;
  beneficiaire: BeneficiaireQuotePart;
  libelleBeneficiaire: string;
  exercice: number;
  trimestre: number;
  montant: number;
  date: string;
  reference: string | null;
  commentaire: string | null;
  annule: boolean;
  motifAnnulation: string | null;
  saisiParNom: string;
  createdAt: string;
}

// ------------------------------------------------------------------ suivi budgétaire à l'écran (pilotage, maquette « Pilotage budgétaire »)
export type TypePeriodeRapport = 'CUMUL' | 'MOIS' | 'TRIMESTRE';
export type ComparaisonRapport = 'AUCUNE' | 'N-1' | 'T-1';

export interface FiltresPilotage {
  exercice: number;
  type: TypePeriodeRapport;
  mois?: number;
  trimestre?: number;
  structureCode?: string;
  serviceCode?: string;
  comparaison: ComparaisonRapport;
}

export interface PeriodeExecution {
  type: TypePeriodeRapport;
  exercice: number;
  moisDebut: number;
  moisFin: number;
  debut: string;
  fin: string;
  libelle: string;
  slug: string;
  enCours: boolean;
  fractionEcoulee: number;
  moisPrevus: number;
}

export type CodeAlerteExecution = 'DEPASSEMENT' | 'RETARD' | 'AVANCE' | 'NON_PAYE_ANCIEN';
export interface AlerteExecution {
  code: CodeAlerteExecution;
  detail: string;
}

export interface IndicateursExecution {
  budgetInitial: number;
  budgetRevise: number;
  engage: number;
  liquide: number;
  paye: number;
  tauxEngagement: number | null;
  tauxExecution: number | null;
  engageNonPaye: number;
  soldeDisponible: number;
  prevuPeriode: number;
  engagePeriode: number;
  payePeriode: number;
  realisePrevu: number | null;
  payeN1Periode: number | null;
}

export interface LigneExecution extends IndicateursExecution {
  id: string;
  code: string;
  libelle: string;
  structureCode: string;
  structureLibelle: string;
  serviceCode: string;
  serviceLibelle: string;
  programme: string;
  activiteCode: string;
  activiteLibelle: string;
  source: string;
  alertes: AlerteExecution[];
  nonPayesAnciens: number;
}

export interface GroupeExecution extends IndicateursExecution {
  code: string;
  libelle: string;
  lignes: number;
  alertes: AlerteExecution[];
  nombreLignesEnAlerte: number;
}

export interface MoisExecution {
  mois: number;
  libelle: string;
  prevu: number;
  paye: number;
  engage: number;
  payeN1: number | null;
  prevuCumul: number;
  payeCumul: number;
  payeN1Cumul: number | null;
}

export interface DonneesExecution {
  exercice: number;
  periode: PeriodeExecution;
  perimetre: { code: string; libelle: string };
  comparaison: ComparaisonRapport;
  comparaisonLibelle: string | null;
  comparaisonCourt: string | null;
  dateSituation: string;
  seuils: { retard: number; avance: number; ancienneteJours: number };
  cles: IndicateursExecution & { prevuCumul: number; realisePrevuCumul: number | null; lignes: number };
  plan: { saisies: number; parDefaut: number; incoherentes: number };
  mensuel: MoisExecution[];
  structures: GroupeExecution[];
  services?: GroupeExecution[];
  masses: GroupeExecution[];
  /** Ventilation par activité (lignes = sous-activités). */
  activites: GroupeExecution[];
  /** Ventilation par axe stratégique du PTBA (« Hors axe » pour les lignes sans axe). */
  axes: GroupeExecution[];
  /** Budget par nature de dépense (lignes de dépense du PTBA) : budget seulement. */
  natures: Array<{ code: string; libelle: string; lignes: number; budget: number; part: number }>;
  lignes: LigneExecution[];
  lignesEnAlerte: LigneExecution[];
  nonPayes: Array<{ id: string; numero: string | null; objet: string; structureCode: string | null; montant: number; dateAutorisation: string; jours: number; etatPaiement: string }>;
  nonPayesTotal: { nombre: number; montant: number; anciens: number; montantAnciens: number };
  commentaires: string[];
  ressources?: SituationRessources;
  /** Périmètres consultables par l'utilisateur (sélecteurs de la page). */
  perimetres: PerimetresRapport;
}

// ------------------------------------------------------------------ budget prévisionnel importé (Google Sheets, Excel, CSV)
export interface AnomalieImport {
  ligne: number;
  code: string | null;
  message: string;
}

export interface RapportImportPrevisionnel {
  exercice: number;
  simulation: boolean;
  origine: string;
  lignes: { lues: number; creees: number; modifiees: number; inchangees: number; absentes: string[] };
  plans: { enregistres: number; ignores: number };
  totaux: { budgetPrevisionnel: number; budgetActuel: number };
  erreurs: AnomalieImport[];
  avertissements: AnomalieImport[];
  ecrit: boolean;
}

/** Classeur Google Sheets de référence du budget prévisionnel d'un exercice et état de la dernière synchronisation. */
export interface ResumeSynchronisation {
  date: string;
  etat: 'OK' | 'ERREUR';
  origine: 'manuelle' | 'automatique';
  message?: string;
  lignes?: RapportImportPrevisionnel['lignes'];
  plans?: RapportImportPrevisionnel['plans'];
  totaux?: RapportImportPrevisionnel['totaux'];
  avertissements?: number;
  erreurs?: number;
}

export interface SourcePrevisionnel {
  exercice: number;
  url: string | null;
  automatique: boolean;
  derniereSynchronisation: string | null;
  dernierEtat: 'OK' | 'ERREUR' | null;
  dernierResume: ResumeSynchronisation | null;
  modifieParNom: string | null;
}

// ------------------------------------------------------------------ démarches RH (spécifications démarches RH v1.4, lot RH-1)
export type RegimeConge = 'CADRE_DIRECTION' | 'AGENT';
export const LIBELLE_REGIME: Record<RegimeConge, string> = { CADRE_DIRECTION: 'Cadre de direction (30 jours ouvrables + 1 jour par 2 ans)', AGENT: 'Agent (26 jours ouvrables + 1 jour par 5 ans)' };

export interface ResumeAgentRh {
  id: string;
  matricule: string;
  nom: string;
  prenom: string;
  fonction: string;
  grade: string | null;
  categorie: CategorieAgent;
  structure: { id: string; code: string; libelle: string } | null;
  superieur: { id: string; nom: string; fonction: string } | null;
  dateEngagement: string | null;
  dateAnciennete: string | null;
  regimeConge: RegimeConge | null;
  regimeConfirmeLe: string | null;
  regimePropose: RegimeConge;
  eligibleRh: boolean;
  actif: boolean;
}

export interface FicheRh extends ResumeAgentRh {
  anciennete: { reference: string; annees: number; mois: number } | null;
  douzeMoisDeService: boolean;
  periodeEnCours: { debut: string; fin: string; prochainAnniversaire: string } | null;
  droitAnnuel: { regime: RegimeConge; libelle: string; base: number; majoration: number; total: number } | null;
  droits: { modifierFiche: boolean; voirSituation: boolean; modifierSituation: boolean; voirMentions: boolean; ajouterMention: boolean };
}

export interface SituationRh {
  version: number;
  salaireNet: number | null;
  devise: string;
  dateRetraitePrevue: string | null;
  decompteFinalEstime: number | null;
  dateCalculDecompte: string | null;
  observations: string | null;
  saisiPar: { id: string; nom: string } | null;
  date: string;
}

export interface MentionRh {
  id: string;
  date: string;
  objet: string;
  reference: string | null;
  saisiPar: string | null;
  createdAt: string;
}

export interface EspaceRh {
  fiche: FicheRh;
  situation: SituationRh | null;
  modules: { conges: 'A_VENIR' | 'DISPONIBLE'; prets: 'A_VENIR' | 'DISPONIBLE'; procedures: 'A_VENIR' | 'DISPONIBLE' };
}

export interface JourFerie {
  id: string;
  date: string;
  libelle: string;
}

export interface FicheRhSaisie {
  grade?: string | null;
  superieurId?: string | null;
  dateEngagement?: string | null;
  dateAnciennete?: string | null;
  regimeConge?: RegimeConge | '' | null;
  eligibleRh?: boolean;
}

export interface SituationRhSaisie {
  salaireNet?: number | null;
  devise?: 'USD' | 'CDF';
  dateRetraitePrevue?: string | null;
  decompteFinalEstime?: number | null;
  dateCalculDecompte?: string | null;
  observations?: string | null;
}

// ------------------------------------------------------------------ congés (lot RH-2)
export type CategorieConge = 'ANNUEL' | 'MALADIE' | 'ACCOUCHEMENT' | 'CIRCONSTANCE' | 'ABSENCE_AUTORISEE';
export const CATEGORIES_CONGE: CategorieConge[] = ['ANNUEL', 'MALADIE', 'ACCOUCHEMENT', 'CIRCONSTANCE', 'ABSENCE_AUTORISEE'];
export const LIBELLE_CATEGORIE_CONGE: Record<CategorieConge | 'ABSENCE', string> = { ANNUEL: 'Congé annuel', MALADIE: 'Congé maladie', ACCOUCHEMENT: 'Accouchement (Code du travail, art. 130)', CIRCONSTANCE: 'Congé de circonstance', ABSENCE_AUTORISEE: 'Absence autorisée', ABSENCE: 'Absence' };
export type SousMotifCirconstance = 'MARIAGE_AGENT' | 'MARIAGE_ENFANT' | 'ACCOUCHEMENT_EPOUSE' | 'DECES_CONJOINT' | 'DECES_PARENT_1' | 'DECES_PARENT_2';
export const LIBELLE_SOUS_MOTIF: Record<SousMotifCirconstance, string> = { MARIAGE_AGENT: "Mariage de l'agent", MARIAGE_ENFANT: "Mariage de l'enfant de l'agent", ACCOUCHEMENT_EPOUSE: "Accouchement de l'épouse de l'agent", DECES_CONJOINT: 'Décès du conjoint', DECES_PARENT_1: "Décès d'un parent ou allié au premier degré", DECES_PARENT_2: "Décès d'un parent ou allié au second degré" };
export type StatutConge = 'BROUILLON' | 'SOUMIS' | 'A_CORRIGER' | 'APPROUVE' | 'REJETE' | 'RETIRE' | 'ANNULE' | 'INTERROMPU' | 'PRIS' | 'REMPLACE';
export const LIBELLE_STATUT_CONGE: Record<StatutConge, string> = { BROUILLON: 'Brouillon', SOUMIS: 'Soumis', A_CORRIGER: 'À corriger', APPROUVE: 'Approuvé', REJETE: 'Rejeté', RETIRE: 'Retiré', ANNULE: 'Annulé', INTERROMPU: 'Interrompu', PRIS: 'Pris', REMPLACE: 'Remplacé' };

export interface ResumeConge {
  id: string;
  numero: string | null;
  agent: { id: string; nom: string; matricule: string; fonction: string };
  structure: { id: string; code: string; libelle: string };
  categorie: CategorieConge | 'ABSENCE';
  libelleCategorie: string;
  sousMotif: SousMotifCirconstance | null;
  dateDebut: string;
  dateReprise: string;
  dernierJour: string | null;
  joursOuvrables: number;
  joursPris: number | null;
  statut: StatutConge;
  etapeCourante: Etape | null;
  version: number;
  avantDouzeMois: boolean;
  nonPreste: boolean;
  interimaire: { id: string; nom: string } | null;
  enregistreRhLe: string | null;
  dateSoumission: string | null;
  updatedAt: string;
}

export interface ControleConge {
  ok: boolean;
  message: string;
  bloquant?: boolean;
}

export interface ResumePeriode {
  id: string;
  debut: string;
  fin: string;
  joursBase: number;
  joursMajoration: number;
  anneesAnciennete: number;
  joursReportRecu: number;
  regularisations: number;
  droits: number;
  pris: number;
  approuves: number;
  enAttente: number;
  disponible: number;
  joursAvantFin: number;
  alerteExpiration: boolean;
  cloturee: boolean;
  reportSortant: number | null;
  perte: number | null;
  origine: 'CALCUL' | 'REPRISE' | 'REGULARISATION';
}

export interface SituationDroits {
  regimeConge: RegimeConge | null;
  regle: { base: number; trancheAnnees: number } | null;
  dateEngagement: string | null;
  douzeMoisDeService: boolean;
  droitOuvertLe: string | null;
  periodes: ResumePeriode[];
  courante: ResumePeriode | null;
  joursNonPrestesAnnee: number;
  joursMaladieAnnee: number;
}

export interface InformationsConge {
  certificatDu?: string;
  certificatAu?: string;
  praticien?: string;
  dateAccouchement?: string;
  dateEvenement?: string;
}

export interface ActionsConge {
  modifier: boolean;
  soumettre: boolean;
  resoumettre: boolean;
  retirer: boolean;
  annuler: boolean;
  decider: boolean;
  etape: Etape | null;
  decisions: DecisionApi[];
  enregistrerRh: boolean;
  demanderInterruption: boolean;
  approuverInterruption: boolean;
  nouvelleVersion: boolean;
}

export interface DetailConge {
  conge: ResumeConge & {
    motif: string | null;
    informations: InformationsConge | null;
    interruption: { demandeeParId: string; demandeeLe: string; dateInterruption: string; motif: string; approuveeParId?: string; approuveeLe?: string; joursPris?: number; joursRestitues?: number } | null;
    versionPrecedente: { id: string; numero: string | null; statut: StatutConge } | null;
    versionSuivante: { id: string; numero: string | null; statut: StatutConge } | null;
    dateDecision: string | null;
    periodeId: string | null;
  };
  circuit: Circuit | null;
  circuitPrevu: Etape[];
  actions: ActionsConge;
  controles: ControleConge[];
  solde: { periode: ResumePeriode | null; demandes: number; apres: number | null } | null;
  feries: Array<{ date: string; libelle: string }>;
  peutVoirInformations: boolean;
}

export interface CalculConge {
  joursOuvrables: number;
  dernierJour: string | null;
  feries: Array<{ date: string; libelle: string }>;
  avantDouzeMois: boolean;
  nonPreste: boolean;
  solde: { periode: ResumePeriode | null; demandes: number; apres: number | null };
  circuitPrevu: Etape[];
  controles: ControleConge[];
}

export interface CongeSaisie {
  categorie: CategorieConge;
  sousMotif?: SousMotifCirconstance;
  motif?: string;
  informations?: InformationsConge;
  dateDebut: string;
  dateReprise: string;
  interimaireId?: string | null;
}

export interface EvenementCalendrier {
  id: string;
  type: 'ENVISAGE' | 'SOUMIS' | 'APPROUVE' | 'PRIS';
  agent: { id: string; nom: string; structureCode: string | null };
  debut: string;
  fin: string;
  jours: number | null;
  categorie: CategorieConge | 'ABSENCE' | null;
  libelle: string;
}

export interface IntentionConge {
  id: string;
  agentId: string;
  debut: string;
  fin: string;
  commentaire: string | null;
}

export interface SoldeAgentRh {
  agent: { id: string; matricule: string; nom: string; prenom: string; fonction: string; structureCode: string | null };
  regimeConge: RegimeConge | null;
  dateEngagement: string | null;
  douzeMoisDeService: boolean;
  courante: ResumePeriode | null;
}

export interface EtatNonPrestes {
  etat: { id: string; mois: string; statut: 'PREPARE' | 'TRANSMIS' | 'CONFIRME'; prepareLe: string; transmisLe: string | null; confirmeLe: string | null } | null;
  lignes: Array<{ id: string; congeId: string; agentId: string; jours: number; joursConfirmes: number | null; ecartMotif: string | null; agent: { nom: string; matricule: string; structureCode: string | null }; conge: { numero: string | null; dateDebut: string; dateReprise: string } }>;
  apercu: number;
}

export interface BlocPrets {
  demande: { id: string; numero: string | null; statut: StatutPret; libelleStatut: string; etape: Etape | null; conditionsEnAttente: boolean } | null;
  plafond: { montant: number; devise: DevisePret; valideJusquAu: string; expire: boolean } | null;
  aTraiter: number;
  plafondsACalculer: number | null;
  aAnalyser: number | null;
  eligible: boolean;
  pret: { id: string; demandeId: string | null; numero: string; statut: StatutPretAccorde; libelleStatut: string; capitalRestant: number; devise: DevisePret; prochaineEcheance: { date: string; mensualite: number } | null; nonRetenues: number } | null;
  portefeuille: { pretsEnForce: number; encours: number; nonRetenues: number; recouvrements: number; aDecaisser: number } | null;
}

// ------------------------------------------------------------------ Prêts sociaux : décaissement et remboursement (lot RH-4)
export type StatutPretAccorde = 'A_DECAISSER' | 'DECAISSE' | 'EN_REMBOURSEMENT' | 'REAMENAGE' | 'EN_RECOUVREMENT' | 'SOLDE';
export const LIBELLE_STATUT_PRET_ACCORDE: Record<StatutPretAccorde, string> = { A_DECAISSER: 'À décaisser', DECAISSE: 'Décaissé', EN_REMBOURSEMENT: 'En remboursement', REAMENAGE: 'Réaménagé', EN_RECOUVREMENT: 'En recouvrement', SOLDE: 'Soldé' };
export type StatutEcheance = 'PREVUE' | 'PREPAREE' | 'CONFIRMEE' | 'PARTIELLE' | 'NON_RETENUE' | 'ANNULEE';
export type SourceRemboursement = 'RETENUE' | 'ANTICIPE' | 'DIRECT' | 'REGULARISATION' | 'DECOMPTE_FINAL' | 'RECOUVREMENT' | 'REPRISE';
export const TYPES_ETAPE_RECOUVREMENT = ['MISE_EN_DEMEURE', 'RELANCE', 'ACCORD', 'ENCAISSEMENT', 'TRANSMISSION_JURIDIQUE', 'AUTRE'] as const;
export type TypeEtapeRecouvrement = (typeof TYPES_ETAPE_RECOUVREMENT)[number] | 'OUVERTURE' | 'CLOTURE';
export const LIBELLE_ETAPE_RECOUVREMENT: Record<TypeEtapeRecouvrement, string> = { OUVERTURE: 'Ouverture', MISE_EN_DEMEURE: 'Mise en demeure', RELANCE: 'Relance', ACCORD: 'Accord de remboursement', ENCAISSEMENT: 'Encaissement', TRANSMISSION_JURIDIQUE: 'Transmission au service juridique', AUTRE: 'Autre', CLOTURE: 'Clôture' };

export interface SoldePret {
  capitalRestant: number;
  capitalRembourse: number;
  interetsPayes: number;
  interetsPrevus: number;
  interetsEchus: number;
  totalRembourse: number;
  prochaineEcheance: { numero: number; date: string; mensualite: number } | null;
  nonRetenues: number;
  echeancesConfirmees: number;
  echeancesTotal: number;
}

/** Situation arrêtée reprise à la mise en service (décision du DG du 4 novembre 2026 : 31 décembre 2026). */
export interface SituationReprisePret {
  date: string;
  capitalRestantDu: number;
  echeancesRestantes: number;
  echeancesPayees: number;
  interetsPayes: number;
  mensualiteClasseur: number | null;
  observations: string | null;
  repriseParId: string;
  repriseLe: string;
}

export interface ResumePretAccorde {
  id: string;
  numero: string;
  demandeId: string | null;
  agent: { id: string; nom: string; matricule: string; fonction: string };
  structure: { id: string; code: string; libelle: string };
  nature: NaturePret;
  libelleNature: string;
  devise: DevisePret;
  montantTotal: number;
  montantRachat: number;
  montantNet: number;
  tauxAnnuel: number;
  dureeMois: number;
  mensualite: number;
  statut: StatutPretAccorde;
  libelleStatut: string;
  banque: string | null;
  referencePaiement: string | null;
  dateDecaissement: string | null;
  premiereEcheance: string | null;
  versionEcheancier: number;
  fedId: string | null;
  dateExigibilite: string | null;
  situation: SituationReprisePret | null;
  solde: SoldePret;
}

export interface EcheanceVue extends EcheancePret {
  id: string;
  version: number;
  statut: StatutEcheance;
  libelleStatut: string;
  montantConstate: number;
}

export interface RemboursementVue {
  id: string;
  date: string;
  montant: number;
  capital: number;
  interets: number;
  source: SourceRemboursement;
  libelleSource: string;
  reference: string | null;
  commentaire: string | null;
  echeanceNumero: number | null;
  saisiPar: string;
  contrepassationDeId: string | null;
  contrepasse: boolean;
  createdAt: string;
}

export interface EtapeRecouvrement {
  date: string;
  type: TypeEtapeRecouvrement;
  commentaire: string;
  montant?: number;
  parId: string;
  parNom: string;
}

export interface RecouvrementVue {
  id: string;
  pretId: string;
  numero: string;
  agent: { id: string; nom: string; matricule: string };
  dateExigibilite: string;
  motif: string;
  montantExigible: number;
  imputeDecompte: number;
  reliquat: number;
  encaisse: number;
  restant: number;
  statut: 'OUVERT' | 'EN_COURS' | 'CLOS';
  etapes: EtapeRecouvrement[];
  decisionCloture: string | null;
  closLe: string | null;
  anciennete: number;
}

export interface ActionsPortefeuille {
  etablirFed: boolean;
  constaterDecaissement: boolean;
  enregistrerExtinction: boolean;
  enregistrerRemboursement: boolean;
  contrepasser: boolean;
  proposerReamenagement: boolean;
  repondreReamenagement: boolean;
  approuverReamenagement: boolean;
  declarerExigibilite: boolean;
  proposerImputation: boolean;
  validerImputation: 'DRH' | 'DF' | 'DG' | null;
  etapeRecouvrement: boolean;
  cloturerRecouvrement: boolean;
}

export interface DetailPretAccorde {
  pret: ResumePretAccorde & {
    reamenagement: { dureeMois: number; mensualite: number; capitalRestant: number; commentaire: string; proposeParId: string; proposeLe: string; accepteLe?: string; refuseLe?: string; approuveParId?: string; approuveLe?: string } | null;
    imputation: { montant: number; commentaire: string | null; proposeParId: string; proposeLe: string; validations: Partial<Record<'DRH' | 'DF' | 'DG', { parId: string; le: string }>>; executeeLe?: string } | null;
    motifExigibilite: string | null;
    compteId: string | null;
    origine: 'CALCUL' | 'REPRISE';
    soldeLe: string | null;
  };
  fed: { id: string; numero: string | null; statut: string; etatPaiement: string; datePaiement: string | null; montantUsd: number } | null;
  echeancier: EcheanceVue[];
  versions: number[];
  remboursements: RemboursementVue[];
  retenues: Array<{ mois: string; statut: string; montantPrepare: number; montantConfirme: number | null; ecartMotif: string | null }>;
  engagementsRachetes: Array<{ id: string; source: string; banque: string | null; soldeRestantDu: number; devise: string; statut: string; extinction: { date: string; reference: string } | null }>;
  recouvrement: RecouvrementVue | null;
  actions: ActionsPortefeuille;
  clause: string | null;
}

export interface IndicateursPortefeuille {
  demandes: Record<string, number>;
  delaiMoyenJours: number | null;
  tauxApprobation: number | null;
  tauxRejet: number | null;
  montantsDemandes: number;
  montantsApprouves: number;
  montantsDecaisses: { net: number; rachat: number; total: number };
  capitalRestantDu: number;
  interetsPrevisionnels: number;
  interetsEchus: number;
  interetsEncaisses: number;
  echeancesNonRetenues: number;
  creancesEnRecouvrement: { nombre: number; montant: number };
  pretsEnForce: number;
  aDecaisser: number;
  rendement: Array<{ banque: string; pretsEnForce: number; encours: number; interetsACollecter: number; interetsEncaisses: number }>;
}

export interface EtatRetenuesPrets {
  etat: { id: string; mois: string; statut: 'PREPARE' | 'TRANSMIS' | 'CONFIRME'; prepareLe: string; transmisLe: string | null; confirmeLe: string | null } | null;
  lignes: Array<{ id: string; pretId: string; demandeId: string | null; numero: string; agent: { id: string; nom: string; matricule: string }; structureCode: string; banque: string | null; devise: DevisePret; montantDecaisse: number; capitalRestant: number; echeance: { numero: number; date: string; statut: StatutEcheance } | null; principal: number; interets: number; montantPrepare: number; montantConfirme: number | null; ecartMotif: string | null; derniereEcheance: string }>;
  apercu: number;
  total: { montant: number; principal: number; interets: number };
}

export interface BlocRh {
  solde: number | null;
  dateLimite: string | null;
  alerteExpiration: boolean;
  prochaineAbsence: { id: string; debut: string; reprise: string; libelle: string } | null;
  demandesEnCours: number;
  aTraiter: number;
  rh: { regimesAConfirmer: number; aEnregistrer: number; interruptions: number } | null;
  prets: BlocPrets;
  procedures: BlocProcedures;
  hrms: BlocHrms;
  paie: BlocPaie;
  presences: BlocPresences;
  calculs: BlocCalculPaie | null;
}

// ------------------------------------------------------------------ Prêts sociaux (démarches RH v1.4, lot RH-3)
export const NATURES_PRET = ['CONSOMMATION', 'INVESTISSEMENT'] as const;
export type NaturePret = (typeof NATURES_PRET)[number];
export const LIBELLE_NATURE_PRET: Record<NaturePret, string> = { CONSOMMATION: 'Prêt à la consommation', INVESTISSEMENT: "Prêt d'investissement" };
export const DEVISES_PRET = ['USD', 'CDF'] as const;
export type DevisePret = (typeof DEVISES_PRET)[number];
export type StatutPret = 'PLAFOND_DEMANDE' | 'PLAFOND_COMMUNIQUE' | 'SIMULATION' | 'SOUMIS' | 'A_CORRIGER' | 'SUSPENDU' | 'APPROUVE' | 'REJETE' | 'ANNULE' | 'DECAISSE' | 'EN_REMBOURSEMENT' | 'SOLDE';
export const LIBELLE_STATUT_PRET: Record<StatutPret, string> = {
  PLAFOND_DEMANDE: 'Plafond à vérifier',
  PLAFOND_COMMUNIQUE: 'Plafond communiqué',
  SIMULATION: 'Simulation',
  SOUMIS: 'Soumise',
  A_CORRIGER: 'Complément demandé',
  SUSPENDU: 'Suspendue',
  APPROUVE: 'Approuvée',
  REJETE: 'Rejetée',
  ANNULE: 'Annulée',
  DECAISSE: 'Décaissé',
  EN_REMBOURSEMENT: 'En remboursement',
  SOLDE: 'Soldé',
};
export type SensAvis = 'FAVORABLE' | 'DEFAVORABLE';
export const MOTIFS_AVIS = ['AUTRES_PRETS', 'RATIO', 'DISCIPLINE', 'RETRAITE', 'GARANTIE', 'AUTRE'] as const;
export type MotifAvis = (typeof MOTIFS_AVIS)[number];
export const LIBELLE_MOTIF_AVIS: Record<MotifAvis, string> = {
  AUTRES_PRETS: 'Autre(s) prêt(s) en cours',
  RATIO: "Ratio d'endettement supérieur ou égal au seuil (33 % du salaire net)",
  DISCIPLINE: 'Question disciplinaire',
  RETRAITE: 'Dernière échéance postérieure au départ à la retraite',
  GARANTIE: 'Garantie insuffisante (décompte final)',
  AUTRE: 'Autre motif (précisé dans le commentaire)',
};
export type SourceEngagement = 'BANQUE' | 'FONAREV';
export type StatutEngagement = 'EN_COURS' | 'RACHAT_PREVU' | 'ETEINT';

export interface EngagementSaisie {
  source: SourceEngagement;
  banque?: string | null;
  montant: number;
  devise: DevisePret;
  mensualite: number;
  maturiteMois: number;
  dateDecaissement?: string | null;
  soldeRestantDu: number;
  rachatPrevu?: boolean;
}

export interface DemandePretSaisie {
  nature: NaturePret;
  devise: DevisePret;
  montantSouhaite: number;
  objet?: string | null;
  engagements?: EngagementSaisie[];
}

export interface EngagementVue extends EngagementSaisie {
  id: string;
  banque: string | null;
  dateDecaissement: string | null;
  statut: StatutEngagement;
  verifieLe: string | null;
  extinction: { date: string; reference: string } | null;
}

export interface ResumePret {
  id: string;
  numero: string | null;
  agent: { id: string; nom: string; matricule: string; fonction: string };
  structure: { id: string; code: string; libelle: string };
  nature: NaturePret;
  libelleNature: string;
  devise: DevisePret;
  montantSouhaite: number;
  montantConfirme: number | null;
  dureeMois: number | null;
  mensualite: number | null;
  statut: StatutPret;
  libelleStatut: string;
  etapeCourante: Etape | null;
  conditionsEnAttente: number | null;
  instructeur: { id: string; nom: string } | null;
  dateDepot: string;
  datePlafond: string | null;
  dateSoumission: string | null;
  dateDecision: string | null;
  plafond: { plafondDisponible: number; valideJusquAu: string; expire: boolean } | null;
}

export interface PlafondVue {
  id: string;
  decompteFinal: number;
  quotite: number;
  capaciteMobilisee: number;
  plafondDisponible: number;
  devise: DevisePret;
  dateCalcul: string;
  valideJusquAu: string;
  expire: boolean;
  observations: string | null;
  calculePar: string;
  detail: Record<string, unknown> | null;
}

export interface SimulationVue {
  id: string;
  montant: number;
  dureeMois: number;
  tauxAnnuel: number;
  mensualite: number;
  totalInterets: number;
  total: number;
  partSalaire: number | null;
  partDecompte: number | null;
  dansPlafond: boolean;
  capaciteOk: boolean | null;
  retenue: boolean;
  premiereEcheance: string;
  derniereEcheance: string;
  createdAt: string;
}

export interface ConditionsVue {
  id: string;
  version: number;
  montant: number;
  dureeMois: number;
  tauxAnnuel: number;
  mensualite: number;
  fraisDossier: number;
  totalInterets: number;
  total: number;
  montantRachat: number;
  montantNet: number;
  auteur: 'AGENT' | 'DIRECTEUR_FINANCIER' | 'DIRECTEUR_GENERAL';
  auteurNom: string;
  commentaire: string | null;
  clauseVersion: number;
  clauseTexte: string;
  proposeeLe: string;
  accepteeLe: string | null;
  refuseeLe: string | null;
}

export interface AvisVue {
  id: string;
  etape: Etape;
  auteur: string;
  titulaire: string | null;
  sens: SensAvis;
  motifs: MotifAvis[];
  libellesMotifs: string[];
  commentaire: string | null;
  date: string;
}

export interface ChiffresAnalyse {
  devise: DevisePret;
  salaireNet: number | null;
  seuilCapacite: number;
  capaciteMax: number | null;
  mensualitesFonarev: number;
  mensualitesBanques: number;
  mensualitesMaintenues: number;
  partMensualitesMaintenues: number | null;
  capaciteResiduelle: number | null;
  decompteFinal: number | null;
  quotite: number;
  encoursFonarev: number;
  encoursBanques: number;
  encoursTotal: number;
  capaciteResiduelleEndettement: number | null;
  montantSollicite: number;
  partMontantSollicite: number | null;
  mensualiteSollicitee: number;
  totalARembourserSollicite: number;
  montantTotal: number;
  montantRachat: number;
  montantNet: number;
  totalARembourser: number;
  totalInterets: number;
  mensualite: number;
  maturite: number;
  tauxAnnuel: number;
  partSalaire: number | null;
  partDecompte: number | null;
  dateRetraitePrevue: string | null;
  derniereEcheance: string;
}

export interface AnalyseVue {
  id: string;
  version: number;
  chiffres: ChiffresAnalyse;
  avis: SensAvis;
  commentaire: string;
  analyste: string;
  analysteFonction: string;
  date: string;
}

export interface EcheancePret {
  numero: number;
  date: string;
  capitalDebut: number;
  interets: number;
  principal: number;
  mensualite: number;
  capitalFin: number;
}

export interface ActionsPret {
  modifierEngagements: boolean;
  saisirPlafond: boolean;
  simuler: boolean;
  confirmer: boolean;
  retirer: boolean;
  resoumettre: boolean;
  decider: boolean;
  etape: Etape | null;
  decisions: DecisionApi[];
  avisRequis: boolean;
  avisDefavorableRequis: boolean;
  affecter: boolean;
  analyser: boolean;
  modifierConditions: boolean;
  repondreConditions: boolean;
  suspendre: boolean;
  reprendre: boolean;
}

export interface DetailPret {
  demande: ResumePret & { objet: string | null; observations: string | null; motifSuspension: string | null; suspenduLe: string | null; motifCloture: string | null; dateComplement: string | null; dateReceptionDf: string | null; tauxAnnuel: number | null };
  identite: { nom: string; matricule: string; fonction: string; grade: string | null; structure: string; dateEngagement: string | null; moisDeService: number | null };
  eligibilite: { eligible: boolean; motif: string | null };
  situation: { salaireNet: number | null; devise: string; dateRetraitePrevue: string | null; decompteFinalEstime: number | null; dateCalculDecompte: string | null; version: number } | null;
  questionDisciplinaire: boolean | null;
  engagements: EngagementVue[];
  plafond: PlafondVue | null;
  plafonds: number;
  simulations: SimulationVue[];
  conditions: ConditionsVue[];
  conditionsCourantes: ConditionsVue | null;
  conditionsEnAttente: ConditionsVue | null;
  avis: AvisVue[];
  analyse: AnalyseVue | null;
  analyses: AnalyseVue[];
  controles: ControleConge[];
  chiffres: ChiffresAnalyse | null;
  echeancier: EcheancePret[] | null;
  montantEnLettres: string | null;
  clause: { version: number; texte: string };
  circuit: Circuit | null;
  circuitPrevu: Etape[];
  actions: ActionsPret;
  parametres: { tauxAnnuel: number; dureeMaxMois: number; seuilCapacite: number; quotiteDefaut: number; quotiteMax: number; jourLimiteDecaissement: number; fraisDossier: number; validitePlafondJours: number };
}

export interface ParametresPrets {
  tauxAnnuel: Record<NaturePret, number>;
  dureeMaxMois: number;
  fraisDossier: number;
  quotiteDefaut: number;
  quotiteMax: number;
  validitePlafondJours: number;
  seuilCapacite: number;
  seuilBloquant: boolean;
  tauxCdfUsd: number | null;
  tauxCdfUsdDate: string | null;
  jourLimiteDecaissement: number;
  ancienneteMinimaleMois: number;
  delaiComplementJoursOuvrables: number;
  clause: { version: number; texte: string };
  eligibilite: { eligible: boolean; motif: string | null };
}

export interface ResultatSimulation {
  simulation: SimulationVue;
  echeancier: EcheancePret[];
  controles: ControleConge[];
  chiffres: ChiffresAnalyse | null;
}

export interface EcheancierPret {
  titre: string;
  montant: number;
  dureeMois: number;
  tauxAnnuel: number;
  devise: DevisePret;
  echeances: EcheancePret[];
}

// ------------------------------------------------------------------ Bibliothèque des procédures (démarches RH v1.4, § 6 ; lot RH-5)
export type StatutProcedure = 'BROUILLON' | 'EN_VALIDATION' | 'PUBLIEE' | 'ARCHIVEE';
export const LIBELLE_STATUT_PROCEDURE: Record<StatutProcedure, string> = { BROUILLON: 'Brouillon', EN_VALIDATION: 'En validation', PUBLIEE: 'Publiée', ARCHIVEE: 'Archivée' };
export const TYPES_CIBLE = ['TOUS', 'STRUCTURE', 'FONCTION', 'PROFIL', 'AGENT'] as const;
export type TypeCible = (typeof TYPES_CIBLE)[number];
export const LIBELLE_TYPE_CIBLE_PROCEDURE: Record<TypeCible, string> = { TOUS: 'Tous les agents', STRUCTURE: 'Direction ou structure', FONCTION: 'Catégorie de fonction', PROFIL: 'Profil applicatif', AGENT: 'Agent nommé' };
export const STATUTS_CONSULTATION = ['A_CONSULTER', 'LU', 'A_RELIRE', 'CONFIRMATION_ATTENDUE', 'CONFIRMEE'] as const;
export type StatutConsultation = (typeof STATUTS_CONSULTATION)[number];
export const LIBELLE_STATUT_CONSULTATION: Record<StatutConsultation, string> = { A_CONSULTER: 'À consulter', LU: 'Lu', A_RELIRE: 'À relire — Nouvelle version', CONFIRMATION_ATTENDUE: 'Confirmation attendue', CONFIRMEE: 'Confirmée' };
export type EtapeValidationProcedure = 'DIRECTEUR' | 'DG';
export const LIBELLE_CATEGORIE_AGENT: Record<CategorieAgent, string> = { PCA: 'Président du Conseil d’administration', DG: 'Directeur Général', DGA: 'Directeur Général Adjoint', ADM: 'Administrateur', CAC: 'Commissaire aux comptes', DIR: 'Directeur', SDIR: 'Sous-directeur', CDIV: 'Chef de division', CSERV: 'Chef de service', CBUR: 'Chef de bureau', ACOL: 'Agent de collaboration', AEXE: 'Agent d’exécution' };

export interface DomaineProcedure {
  id: string;
  code: string;
  libelle: string;
  ordre: number;
  actif: boolean;
}

export interface StatutPersonnelProcedure {
  concerne: boolean;
  statut: StatutConsultation | null;
  libelleStatut: string | null;
  premiereConsultation: string | null;
  derniereConsultation: string | null;
  nombre: number;
  dureeCumulee: number;
  confirmeeLe: string | null;
  echeance: string | null;
  enRetard: boolean;
}

export interface ResumeProcedure {
  id: string;
  reference: string;
  titre: string;
  description: string | null;
  domaine: DomaineProcedure;
  structureResponsable: { id: string; code: string; libelle: string };
  responsable: { id: string; nom: string; fonction: string };
  statut: StatutProcedure;
  libelleStatut: string;
  confirmationRequise: boolean;
  telechargementAutorise: boolean;
  impressionAutorisee: boolean;
  visaDg: boolean;
  motsCles: string[];
  etiquette: string;
  commune: boolean;
  versionEnVigueur: { id: string; numero: number; datePublication: string | null; dateEntreeVigueur: string | null; echeanceDate: string | null } | null;
  personnel: StatutPersonnelProcedure | null;
  updatedAt: string;
}

export interface DocumentProcedure {
  id: string;
  nomFichier: string;
  typeMime: string;
  taille: number;
  version: number;
  createdAt: string;
  pdf: boolean;
}

export interface ValidationsVersionProcedure {
  directeur?: { parId: string; parNom: string; le: string; commentaire: string | null };
  dg?: { parId: string; parNom: string; le: string; commentaire: string | null };
  renvoi?: { parId: string; parNom: string; le: string; motif: string };
}

export interface VersionProcedure {
  id: string;
  numero: number;
  statut: StatutProcedure;
  libelleStatut: string;
  notes: string | null;
  echeanceConsultationJours: number | null;
  dateEntreeVigueur: string | null;
  datePublication: string | null;
  echeanceDate: string | null;
  enVigueur: boolean;
  archiveeLe: string | null;
  soumiseLe: string | null;
  validations: ValidationsVersionProcedure | null;
  document: DocumentProcedure | null;
  annexes: DocumentProcedure[];
  etapeAttendue: EtapeValidationProcedure | null;
  validateursAttendus: string[];
}

export interface ActionsProcedure {
  modifier: boolean;
  nouvelleVersion: boolean;
  modifierVersion: string | null;
  soumettre: string | null;
  valider: { versionId: string; etape: EtapeValidationProcedure } | null;
  archiver: boolean;
  relancer: boolean;
  lire: boolean;
  confirmer: boolean;
  telecharger: boolean;
  imprimer: boolean;
  suivre: boolean;
}

export interface DetailProcedure {
  procedure: ResumeProcedure;
  versions: VersionProcedure[];
  cibles: Array<{ id: string; type: TypeCible; valeur: string | null; libelle: string }>;
  concernes: number | null;
  personnel: StatutPersonnelProcedure | null;
  actions: ActionsProcedure;
  delaiAffichageSecondes: number;
}

export interface BlocProcedures {
  aConsulter: number;
  lues: number;
  nouvellesVersions: number;
  confirmationsAttendues: number;
  aValider: number;
  enRedaction: number;
  suivi: { publiees: number; tauxConsultation: number | null } | null;
  actions: { gerer: boolean; suivre: boolean; administrer: boolean };
}

export interface CibleSaisie {
  type: TypeCible;
  valeur?: string | null;
}

export interface ProcedureSaisie {
  reference: string;
  titre: string;
  domaineId: string;
  description?: string | null;
  structureResponsableId: string;
  responsableId: string;
  motsCles?: string[];
  confirmationRequise?: boolean;
  telechargementAutorise?: boolean;
  impressionAutorisee?: boolean;
  visaDg?: boolean;
  cibles?: CibleSaisie[];
}

export interface VersionProcedureSaisie {
  notes?: string | null;
  echeanceConsultationJours?: number | null;
  dateEntreeVigueur?: string | null;
}

export interface FiltreSuiviProcedures {
  structure?: string;
  domaineId?: string;
  procedureId?: string;
  agentId?: string;
  statut?: StatutConsultation;
  depuis?: string;
  jusqua?: string;
}

export interface SuiviAgentProcedures {
  agent: { id: string; nom: string; matricule: string; fonction: string };
  structure: { code: string; libelle: string } | null;
  applicables: number;
  lues: number;
  aConsulter: number;
  aRelire: number;
  confirmationsAttendues: number;
  confirmees: number;
  enRetard: number;
  derniereConsultation: string | null;
}

export interface SuiviProcedureLigne {
  procedure: ResumeProcedure;
  concernes: number;
  consultes: number;
  restants: number;
  aRelire: number;
  confirmationsRequises: boolean;
  confirmationsRecues: number;
  confirmationsAttendues: number;
  taux: number | null;
  echeance: string | null;
  enRetard: number;
  relances: number;
}

export interface SuiviProcedureAgents {
  procedure: ResumeProcedure;
  agents: Array<{ id: string; nom: string; matricule: string; structure: string | null; statut: StatutConsultation; libelleStatut: string; premiereConsultation: string | null; confirmeeLe: string | null; rappels: number; dernierRappelLe: string | null }>;
}

export interface ConsolideProcedures {
  publiees: number;
  concernes: number;
  consultes: number;
  tauxGlobal: number | null;
  confirmationsAttendues: number;
  enRetard: number;
  parDirection: Array<{ code: string; libelle: string; agents: number; affectations: number; consultees: number; taux: number | null; enRetard: number }>;
  moinsConsultees: Array<{ id: string; reference: string; titre: string; concernes: number; consultes: number; taux: number | null }>;
  nouvellesPublications: Array<{ id: string; reference: string; titre: string; version: number; datePublication: string | null; echeance: string | null }>;
  evolution: Array<{ mois: string; consultations: number; confirmations: number }>;
}

export interface LectureProcedure {
  statut: StatutConsultation | null;
  concerne: boolean;
  delaiSecondes: number;
  delaiRestant: number;
  personnel: StatutPersonnelProcedure | null;
}

// ------------------------------------------------------------------ Référentiel HRMS (spécifications HRMS et Paie, phase 1, § 5.1 ; lot HP-1)
export type StatutDossierHrms = 'IMPORTE' | 'A_REGULARISER' | 'CONTROLE' | 'VALIDE';
export const LIBELLE_STATUT_DOSSIER_HRMS: Record<StatutDossierHrms, string> = { IMPORTE: 'Importé', A_REGULARISER: 'À régulariser', CONTROLE: 'Contrôlé', VALIDE: 'Validé' };
export type SexeHrms = 'F' | 'M';
export type StatutMaritalHrms = 'CELIBATAIRE' | 'MARIE' | 'DIVORCE' | 'VEUF';
export const LIBELLE_STATUT_MARITAL: Record<StatutMaritalHrms, string> = { CELIBATAIRE: 'Célibataire', MARIE: 'Marié(e)', DIVORCE: 'Divorcé(e)', VEUF: 'Veuf(ve)' };
export type LienChargeHrms = 'CONJOINT' | 'ENFANT' | 'AUTRE';
export const LIBELLE_LIEN_CHARGE: Record<LienChargeHrms, string> = { CONJOINT: 'Conjoint(e)', ENFANT: 'Enfant', AUTRE: 'Autre personne à charge' };
export type StatutChargeHrms = 'DECLAREE' | 'VALIDEE' | 'REFUSEE';
export const LIBELLE_STATUT_CHARGE: Record<StatutChargeHrms, string> = { DECLAREE: 'Déclarée, à valider', VALIDEE: 'Validée', REFUSEE: 'Refusée' };
export type TypeContratHrms = 'CDI' | 'CDD' | 'STAGE' | 'CONSULTANT' | 'MANDAT' | 'AUTRE';
export const LIBELLE_TYPE_CONTRAT: Record<TypeContratHrms, string> = { CDI: 'Contrat à durée indéterminée', CDD: 'Contrat à durée déterminée', STAGE: 'Stage', CONSULTANT: 'Consultance', MANDAT: 'Mandat', AUTRE: 'Autre' };
export type EvenementContratHrms = 'ENTREE' | 'AVENANT' | 'RENOUVELLEMENT' | 'SUSPENSION' | 'REPRISE' | 'FIN';
export const LIBELLE_EVENEMENT_CONTRAT: Record<EvenementContratHrms, string> = { ENTREE: 'Entrée', AVENANT: 'Avenant', RENOUVELLEMENT: 'Renouvellement', SUSPENSION: 'Suspension', REPRISE: 'Reprise', FIN: 'Fin de la relation contractuelle' };
export type MotifAffectationHrms = 'ENTREE' | 'MUTATION' | 'PROMOTION' | 'CHANGEMENT' | 'IMPORT';
export const LIBELLE_MOTIF_AFFECTATION: Record<MotifAffectationHrms, string> = { ENTREE: 'Entrée', MUTATION: 'Mutation', PROMOTION: 'Promotion', CHANGEMENT: 'Changement de poste', IMPORT: 'Reprise du fichier HRMS' };
export type ModePaiementHrms = 'VIREMENT' | 'ESPECES' | 'MOBILE';
export const LIBELLE_MODE_PAIEMENT_HRMS: Record<ModePaiementHrms, string> = { VIREMENT: 'Virement bancaire', ESPECES: 'Espèces', MOBILE: 'Paiement mobile' };
export type StatutPaiementHrms = 'PROPOSE' | 'VALIDE' | 'REFUSE' | 'REMPLACE';
export const LIBELLE_STATUT_PAIEMENT_HRMS: Record<StatutPaiementHrms, string> = { PROPOSE: 'À valider par le DRH', VALIDE: 'Validées', REFUSE: 'Refusées', REMPLACE: 'Remplacées' };
export type SectionDossierHrms = 'IDENTITE' | 'AFFECTATION' | 'CONTRAT' | 'ANCIENNETE' | 'FAMILLE' | 'SOCIAL' | 'PAIEMENT' | 'TEMPS_TRAVAIL' | 'PHOTO';
export const LIBELLE_SECTION_DOSSIER: Record<SectionDossierHrms, string> = { IDENTITE: 'Identité et coordonnées', AFFECTATION: 'Affectation', CONTRAT: 'Contrat', ANCIENNETE: 'Ancienneté', FAMILLE: 'Situation familiale', SOCIAL: 'Situation fiscale et sociale', PAIEMENT: 'Paiement', TEMPS_TRAVAIL: 'Temps de travail', PHOTO: 'Photo' };
export type StatutCorrectionHrms = 'OUVERTE' | 'ACCEPTEE' | 'REFUSEE';
export const LIBELLE_STATUT_CORRECTION: Record<StatutCorrectionHrms, string> = { OUVERTE: 'Ouverte', ACCEPTEE: 'Acceptée', REFUSEE: 'Refusée' };
export type ModePointageHrms = 'BADGE' | 'FICHE';
export type StatutPhotoHrms = 'A_VALIDER' | 'VALIDEE';

export interface AnomalieDossierHrms {
  code: string;
  message: string;
}

export interface DossierHrms {
  fiche: FicheRh;
  dossier: {
    id: string;
    statut: StatutDossierHrms;
    libelleStatut: string;
    anomalies: AnomalieDossierHrms[];
    bloquantes: number;
    identite: { nom: string; prenom: string; email: string; sexe: SexeHrms | null; lieuNaissance: string | null; dateNaissance: string | null; dateRetraite: string | null; statutMarital: StatutMaritalHrms | null; telephone: string | null; adresse: string | null; lieuRecrutement: string | null; lieuPrestation: string | null; region: string | null; mutation: boolean; piecesAdministratives: Array<{ type: string; numero: string; validite: string | null }> };
    social: { numeroCnss: string | null; numeroFiscal: string | null; regimeSocial: string | null; dateAffiliationCnss: string | null } | null;
    tempsTravail: { horaireCode: string | null; calendrierCode: string | null; modePointage: ModePointageHrms | null; eligibleHeuresSup: boolean; eligibleAstreinte: boolean };
    photo: { pieceId: string; statut: StatutPhotoHrms | null; url: string } | null;
    famille: { conjointsDeclares: number; enfantsDeclares: number } | null;
    importeLe: string | null;
    sourceImport: string | null;
    controle: { par: string | null; le: string } | null;
  };
  affectations: Array<{ id: string; poste: string; grade: string | null; categorie: CategorieAgent; structure: { id: string; code: string; libelle: string } | null; lieuPrestation: string | null; superieur: { id: string; nom: string | null } | null; motif: MotifAffectationHrms; effetDu: string; effetAu: string | null; commentaire: string | null; creePar: string | null; createdAt: string }>;
  contrats: Array<{ id: string; type: TypeContratHrms; evenement: EvenementContratHrms; debut: string; fin: string | null; reference: string | null; commentaire: string | null; creePar: string | null; createdAt: string }>;
  personnesACharge: Array<{ id: string; lien: LienChargeHrms; nom: string; prenom: string | null; dateNaissance: string | null; pieceId: string | null; statut: StatutChargeHrms; debut: string | null; fin: string | null; motifDecision: string | null; declarePar: string | null; validePar: string | null; valideLe: string | null; createdAt: string }> | null;
  paiements: Array<{ id: string; banque: string | null; compte: string | null; devise: string; mode: ModePaiementHrms; statut: StatutPaiementHrms; saisiPar: string | null; validePar: string | null; valideLe: string | null; motifRefus: string | null; effetDu: string | null; createdAt: string }> | null;
  corrections: Array<{ id: string; section: SectionDossierHrms; libelleSection: string; champ: string; valeurActuelle: string | null; valeurProposee: string; motif: string | null; pieceId: string | null; statut: StatutCorrectionHrms; traitePar: string | null; traiteLe: string | null; reponse: string | null; createdAt: string }>;
  droits: { modifier: boolean; controler: boolean; valider: boolean; voirConfidentiel: boolean; declarerCharge: boolean; validerCharge: boolean; proposerPaiement: boolean; validerPaiement: boolean; demanderCorrection: boolean; traiterCorrection: boolean; deposerPhoto: boolean; validerPhoto: boolean };
}

export interface ResumeDossierHrms {
  agent: { id: string; matricule: string; nom: string; prenom: string; fonction: string; grade: string | null; categorie: CategorieAgent; structure: { code: string; libelle: string } | null; actif: boolean };
  statut: StatutDossierHrms;
  anomalies: number;
  bloquantes: number;
  photo: StatutPhotoHrms | null;
  paiementValide: boolean;
  correctionsOuvertes: number;
  importeLe: string | null;
  controleLe: string | null;
}

export interface ATraiterHrms {
  corrections: Array<{ id: string; agent: { id: string; matricule: string; nom: string; fonction: string }; section: SectionDossierHrms; libelleSection: string; champ: string; valeurActuelle: string | null; valeurProposee: string; motif: string | null; createdAt: string }>;
  paiements: Array<{ id: string; agent: { id: string; matricule: string; nom: string; fonction: string }; banque: string | null; compte: string | null; devise: string; mode: ModePaiementHrms; createdAt: string }>;
  photos: Array<{ agent: { id: string; matricule: string; nom: string; fonction: string }; pieceId: string | null }>;
  charges: Array<{ id: string; agent: { id: string; matricule: string; nom: string; fonction: string }; lien: LienChargeHrms; nom: string; pieceId: string | null; createdAt: string }>;
}

export interface AnomalieImportHrms {
  ligne: number;
  matricule: string | null;
  code: string;
  message: string;
  bloquante: boolean;
}

export interface RapportImportHrms {
  id: string;
  fichier: string;
  dateReference: string;
  simulation: boolean;
  lignes: number;
  crees: number;
  misAJour: number;
  rejetees: number;
  anomalies: AnomalieImportHrms[];
  parCode: Record<string, number>;
  createdAt: string;
}

export interface ResumeImportHrms extends Omit<RapportImportHrms, 'anomalies' | 'parCode'> {
  anomalies: number;
  auteur: string | null;
}

export interface BlocHrms {
  mien: { statut: StatutDossierHrms | null; anomalies: number; photo: StatutPhotoHrms | null; correctionsOuvertes: number };
  rh: { dossiers: number; aRegulariser: number; controles: number; valides: number; corrections: number; paiements: number; photos: number; charges: number; validerPaiements: boolean } | null;
}

export interface IdentiteHrmsSaisie {
  nom?: string;
  prenom?: string;
  email?: string;
  sexe?: SexeHrms | '';
  lieuNaissance?: string | null;
  dateNaissance?: string | null;
  statutMarital?: StatutMaritalHrms | '';
  telephone?: string | null;
  adresse?: string | null;
  lieuRecrutement?: string | null;
  region?: string | null;
  piecesAdministratives?: Array<{ type: string; numero: string; validite: string | null }>;
}
export interface AffectationHrmsSaisie {
  poste: string;
  grade?: string | null;
  categorie: CategorieAgent;
  structureCode: string;
  lieuPrestation?: string | null;
  superieurId?: string | null;
  motif: MotifAffectationHrms;
  effetDu: string;
  commentaire?: string | null;
}
export interface ContratHrmsSaisie {
  type: TypeContratHrms;
  evenement: EvenementContratHrms;
  debut: string;
  fin?: string | null;
  reference?: string | null;
  commentaire?: string | null;
}
export interface PaiementHrmsSaisie {
  banque?: string | null;
  compte?: string | null;
  devise?: 'USD' | 'CDF';
  mode: ModePaiementHrms;
  effetDu?: string | null;
}
export interface PersonneChargeHrmsSaisie {
  lien: LienChargeHrms;
  nom: string;
  prenom?: string | null;
  dateNaissance?: string | null;
  pieceId?: string | null;
}
export interface DemandeCorrectionHrmsSaisie {
  section: SectionDossierHrms;
  champ: string;
  valeurActuelle?: string | null;
  valeurProposee: string;
  motif?: string | null;
  pieceId?: string | null;
}

// ------------------------------------------------------------------ Paie : barème et rémunérations de référence (lot HP-2)
export type StatutVersionBareme = 'PREPARATION' | 'EN_VIGUEUR' | 'ARCHIVEE';
export const LIBELLE_STATUT_VERSION_BAREME: Record<StatutVersionBareme, string> = { PREPARATION: 'En préparation', EN_VIGUEUR: 'En vigueur', ARCHIVEE: 'Archivée' };
export type StatutRemuneration = 'IMPORTEE' | 'A_REGULARISER' | 'CONTROLEE' | 'APPROUVEE';
export const LIBELLE_STATUT_REMUNERATION: Record<StatutRemuneration, string> = { IMPORTEE: 'Importée', A_REGULARISER: 'À régulariser', CONTROLEE: 'Contrôlée (RH)', APPROUVEE: 'Approuvée (DRH, DF)' };

export interface RubriqueRemuneration {
  id: string;
  code: string;
  libelle: string;
  montant: number;
  devise: 'USD' | 'CDF';
  imposable: boolean;
  cotisable: boolean;
  nature: boolean;
  debut: string | null;
  fin: string | null;
  decision: string | null;
  pieceId: string | null;
}
export interface RubriqueRemunerationSaisie {
  code: string;
  libelle: string;
  montant: number;
  devise?: 'USD' | 'CDF';
  imposable: boolean;
  cotisable: boolean;
  nature?: boolean;
  debut?: string | null;
  fin?: string | null;
  decision?: string | null;
  pieceId?: string | null;
}
export interface VersionBaremePaie {
  id: string;
  numero: number;
  libelle: string;
  debut: string;
  fin: string | null;
  statut: StatutVersionBareme;
  libelleStatut: string;
  tauxCnssSalarial: number;
  logementPctBrut: number;
  primeDirecteurImposable: boolean;
  tauxChangeCdfUsd: number | null;
  source: string | null;
  commentaire: string | null;
  lignes: number;
  creePar: string | null;
  approuvePar: string | null;
  approuveLe: string | null;
  createdAt: string;
}
export interface LigneBaremePaie {
  id: string;
  ordre: number;
  categorie: string | null;
  echelon: string | null;
  grade: string;
  cote: string | null;
  echelleIndiciaire: string | null;
  categorieAgent: CategorieAgent | null;
  cadreDirection: boolean;
  brut: number;
  prime: number;
  logement: number;
  logementRegle: boolean;
  transport: number;
  rubriques: RubriqueRemuneration[];
  agents: number;
}
export interface DetailVersionBaremePaie {
  version: VersionBaremePaie;
  lignes: LigneBaremePaie[];
  droits: { modifier: boolean; approuver: boolean; archiver: boolean };
}
export interface ImportBaremeRapport {
  simulation: boolean;
  lignes: number;
  tauxCnss: number | null;
  anomalies: string[];
  grades: Array<{ grade: string; categorie: CategorieAgent; cadreDirection: boolean }>;
}
export interface CorrespondancesGrades {
  version: { id: string; numero: number; libelle: string } | null;
  grades: Array<{ grade: string; agents: number; categories: CategorieAgent[]; correspondance: string | null; ligne: { id: string; grade: string; brut: number } | null; sansLigne: boolean }>;
  lignes: Array<{ id: string; grade: string }>;
}
export interface TrancheImpotSaisie {
  plafondCdf: number | null;
  taux: number;
}
export interface ImpotBareme {
  id: string;
  libelle: string;
  debut: string;
  fin: string | null;
  tranches: TrancheImpotSaisie[];
  plafondPct: number;
  statut: StatutVersionBareme;
  libelleStatut: string;
  source: string | null;
  creePar: string | null;
  approuvePar: string | null;
  approuveLe: string | null;
  createdAt: string;
}
export interface CalculRemuneration {
  brutBareme: number;
  annuitesCumulees: number;
  brutReference: number;
  prime: number;
  primeImposable: boolean;
  rubriques: Array<RubriqueRemuneration & { origine: 'GRADE' | 'INDIVIDUELLE' }>;
  assietteSociale: number;
  tauxCnss: number;
  cnss: number;
  remunerationImposable: number;
  netImposable: number;
  logement: number;
  transport: number;
  impot: { tauxChange: number; baseCdf: number; montantCdf: number; plafonne: boolean; montantUsd: number; tranches: Array<{ taux: number; baseCdf: number; impotCdf: number }> } | null;
  netIndicatif: number | null;
}
export interface FicheRemuneration {
  agent: { id: string; matricule: string; nom: string; prenom: string; fonction: string; grade: string | null; categorie: CategorieAgent; structure: { code: string; libelle: string } | null; dateEngagement: string | null; dateAnciennete: string | null };
  fiche: { id: string; effetDu: string; statut: StatutRemuneration; libelleStatut: string; anomalies: Array<{ code: string; message: string }>; bloquantes: number; motifRevalidation: string | null; cadreDirection: boolean; classe: 'A' | 'B' | null; gradeApplique: string | null; horsBareme: boolean; controle: { par: string | null; le: string } | null; approbationDrh: { par: string | null; le: string } | null; visaDf: { par: string | null; le: string } | null };
  bareme: { versionId: string; numero: number; libelle: string; debut: string; ligne: { id: string; grade: string; categorie: string | null; echelon: string | null; cote: string | null } | null } | null;
  anciennete: { reference: string | null; annees: number | null; mois: number | null; prochainAnniversaire: string | null; classe: 'A' | 'B' | null; tauxApplicable: number | null };
  situationOuverture: { traitementReconnu: number; dateDerniereRevalorisation: string; annuitesIntegrees: number; commentaire: string | null; saisiPar: string | null; saisiLe: string; validePar: string | null; valideLe: string | null } | null;
  calcul: CalculRemuneration;
  annuites: Array<{ id: string; anniversaire: string; anneesRevolues: number; classe: 'A' | 'B' | null; cadreDirection: boolean; taux: number; assietteAvant: number; augmentation: number; assietteApres: number; statut: 'CALCULEE' | 'APPLIQUEE' | 'ANNULEE'; origine: string; commentaire: string | null }>;
  historique: Array<{ id: string; effetDu: string; effetAu: string | null; gradeApplique: string | null; brutBareme: number; annuitesCumulees: number; statut: StatutRemuneration }>;
  droits: { gerer: boolean; controler: boolean; approuver: boolean; viser: boolean; validerSituation: boolean; voirMontants: boolean };
}
export interface ResumeRemuneration {
  agent: { id: string; matricule: string; nom: string; prenom: string; fonction: string; grade: string | null; categorie: CategorieAgent; structure: { code: string; libelle: string } | null };
  statut: StatutRemuneration;
  libelleStatut: string;
  anomalies: number;
  bloquantes: number;
  gradeApplique: string | null;
  ligne: boolean;
  cadreDirection: boolean;
  classe: 'A' | 'B' | null;
  brutBareme: number;
  annuitesCumulees: number;
  brutReference: number;
  prochainAnniversaire: string | null;
  anniversaireProche: boolean;
  situationValidee: boolean;
  effetDu: string;
}
export interface BlocPaie {
  mien: { statut: StatutRemuneration | null; effetDu: string | null };
  paie: { bareme: { id: string; numero: number; libelle: string; debut: string } | null; impot: boolean; fiches: number; aRegulariser: number; controlees: number; approuvees: number; aApprouver: number | null; aViser: number | null; anniversaires30Jours: number; gerer: boolean } | null;
}

// ------------------------------------------------------------------ Paie : présences et variables (lot HP-3)
export type StatutPeriodePresence = 'OUVERTE' | 'CLOTUREE';
export type CategoriePresence = 'PRESTE' | 'MISSION' | 'INCAPACITE' | 'CONGE_ANNUEL' | 'CONGE_CIRCONSTANCE' | 'CONGE_ACCOUCHEMENT' | 'FERIE' | 'REPOS' | 'ABSENCE_AUTORISEE_NON_REMUNEREE' | 'ABSENCE_INJUSTIFIEE' | 'MISE_A_PIED';
export const LIBELLE_CATEGORIE_PRESENCE: Record<CategoriePresence, string> = {
  PRESTE: 'Jour presté',
  MISSION: 'Mission (TDR autorisé)',
  INCAPACITE: 'Incapacité (maladie)',
  CONGE_ANNUEL: 'Congé annuel',
  CONGE_CIRCONSTANCE: 'Congé de circonstance',
  CONGE_ACCOUCHEMENT: 'Congé d’accouchement',
  FERIE: 'Jour férié chômé payé',
  REPOS: 'Repos hebdomadaire',
  ABSENCE_AUTORISEE_NON_REMUNEREE: 'Absence autorisée non rémunérée',
  ABSENCE_INJUSTIFIEE: 'Absence injustifiée',
  MISE_A_PIED: 'Mise à pied',
};
export const CATEGORIES_SAISISSABLES: CategoriePresence[] = ['PRESTE', 'INCAPACITE', 'ABSENCE_AUTORISEE_NON_REMUNEREE', 'ABSENCE_INJUSTIFIEE', 'MISE_A_PIED', 'MISSION'];
export type SourcePresence = 'CALENDRIER' | 'POINTAGE' | 'FICHE' | 'CONGES' | 'MISSION' | 'FERIE' | 'MANUEL';
export const LIBELLE_SOURCE_PRESENCE: Record<SourcePresence, string> = { CALENDRIER: 'Présumé (calendrier)', POINTAGE: 'Pointage', FICHE: 'Fiche de présence', CONGES: 'Module Congés', MISSION: 'Mission (TDR)', FERIE: 'Calendrier RH', MANUEL: 'Correction RH' };
export type TypeHeuresSup = 'SEMAINE' | 'REPOS' | 'FERIE' | 'ASTREINTE';
export const LIBELLE_TYPE_HEURES_SUP: Record<TypeHeuresSup, string> = { SEMAINE: 'Heures supplémentaires de semaine', REPOS: 'Repos hebdomadaire travaillé', FERIE: 'Jour férié travaillé', ASTREINTE: 'Astreinte' };
export type StatutHeuresSup = 'DECLAREE' | 'VALIDEE' | 'REFUSEE';

export interface PeriodePresence {
  id: string;
  mois: string;
  debut: string;
  fin: string;
  dateLimiteVariables: string;
  datePaiement: string | null;
  statut: StatutPeriodePresence;
  libelleStatut: string;
  clotureeLe: string | null;
  commentaire: string | null;
  structuresValidees: number;
  structures: number;
  agents: number;
  anomalies: number;
}
export interface LignePresence {
  date: string;
  categorie: CategoriePresence;
  libelle: string;
  fraction: number;
  source: SourcePresence;
  remunere: boolean;
  heureArrivee: string | null;
  heureDepart: string | null;
  heuresPrestees: number | null;
  anomalie: string | null;
  commentaire: string | null;
  pieceId: string | null;
}
export interface HeuresSupLigne {
  id: string;
  date: string;
  semaine: string;
  type: TypeHeuresSup;
  libelleType: string;
  heures: number;
  motif: string | null;
  statut: StatutHeuresSup;
  declarePar: string | null;
  validePar: string | null;
  valideLe: string | null;
  motifDecision: string | null;
  pieceId: string | null;
}
export interface SynthesePresenceAgent {
  agent: { id: string; matricule: string; nom: string; prenom: string; fonction: string; categorie: CategorieAgent; structure: { id: string; code: string; libelle: string } | null };
  jours: Record<CategoriePresence, number>;
  joursOuvrables: number;
  joursRemuneres: number;
  joursDeduits: number;
  joursSansAllocation: number;
  anomalies: number;
  aConfirmer: number;
  heuresSup: { declarees: number; validees: number; tranche1: number; tranche2: number; reposFerie: number; astreinte: number };
  montantsHeuresSup: { tauxHoraire: number | null; semaine: number | null; reposFerie: number | null; astreinte: number | null; total: number | null } | null;
  validation: 'EN_COURS' | 'VALIDEE';
}
export interface RegistreAgentPresences {
  periode: Omit<PeriodePresence, 'structuresValidees' | 'structures' | 'agents' | 'anomalies'>;
  agent: SynthesePresenceAgent['agent'];
  lignes: LignePresence[];
  heuresSup: HeuresSupLigne[];
  synthese: SynthesePresenceAgent;
  eligibilite: { heuresSup: boolean; astreinte: boolean };
  validation: { statut: 'EN_COURS' | 'VALIDEE'; valideLe: string | null } | null;
  droits: { saisir: boolean; declarerHeuresSup: boolean; validerHeuresSup: boolean; contester: boolean; voirMontants: boolean };
}
export interface MesPresencesData {
  periode: RegistreAgentPresences['periode'] | null;
  agent: SynthesePresenceAgent['agent'] | null;
  lignes: LignePresence[];
  heuresSup: HeuresSupLigne[];
  synthese: SynthesePresenceAgent | null;
  eligibilite: { heuresSup: boolean; astreinte: boolean } | null;
  validation: { statut: 'EN_COURS' | 'VALIDEE'; valideLe: string | null } | null;
  droits: RegistreAgentPresences['droits'] | null;
  periodes: Array<{ id: string; mois: string; statut: StatutPeriodePresence }>;
}
export interface SynthesePresences {
  periode: RegistreAgentPresences['periode'];
  agents: SynthesePresenceAgent[];
  structures: Array<{ structure: { id: string; code: string; libelle: string }; agents: number; anomalies: number; aConfirmer: number; heuresSupDeclarees: number; joursDeduits: number; validation: 'EN_COURS' | 'VALIDEE'; valideLe: string | null; peutValider: boolean }>;
  droits: { gerer: boolean; cloturer: boolean; rouvrir: boolean; voirMontants: boolean };
}
export interface ImportPointagesRapport {
  id: string | null;
  simulation: boolean;
  periode: string;
  lignes: number;
  integrees: number;
  rejetees: number;
  anomalies: Array<{ ligne: number; matricule: string | null; date: string | null; code: string; message: string; bloquante: boolean }>;
}
export interface ResumeImportPointages {
  id: string;
  fichier: string;
  simulation: boolean;
  lignes: number;
  integrees: number;
  rejetees: number;
  anomalies: number;
  auteur: string | null;
  createdAt: string;
}
export interface BlocPresences {
  periode: RegistreAgentPresences['periode'] | null;
  mien: { prestes: number; absences: number; aConfirmer: number; heuresSup: number } | null;
  responsable: { structures: number; aValider: number; anomalies: number; heuresSupDeclarees: number } | null;
  gestion: { structures: number; validees: number; anomalies: number; heuresSupDeclarees: number; importations: number } | null;
}

// ------------------------------------------------------------------ Paie : moteur de calcul (lot HP-4)
export type BlocRubriquePaie = 'PRESENCES' | 'REMUNERATION' | 'HEURES_SUP' | 'INDEMNITES' | 'AUTRES_ELEMENTS' | 'ASSIETTES' | 'RETENUES_LEGALES' | 'AUTRES_DEDUCTIONS' | 'PATRONALES' | 'RESULTAT';
export const LIBELLE_BLOC_RUBRIQUE: Record<BlocRubriquePaie, string> = {
  PRESENCES: 'Présences',
  REMUNERATION: 'Rémunération',
  HEURES_SUP: 'Heures supplémentaires et astreintes',
  INDEMNITES: 'Indemnités',
  AUTRES_ELEMENTS: 'Autres éléments',
  ASSIETTES: 'Assiettes',
  RETENUES_LEGALES: 'Retenues légales',
  AUTRES_DEDUCTIONS: 'Autres déductions',
  PATRONALES: 'Charges patronales',
  RESULTAT: 'Résultat',
};
export type TypeRubriquePaie = 'GAIN' | 'RETENUE' | 'PATRONALE' | 'INFO';
export interface RubriquePaie {
  id: string;
  code: string;
  libelle: string;
  bloc: BlocRubriquePaie;
  libelleBloc: string;
  type: TypeRubriquePaie;
  source: string;
  libelleSource: string;
  imposable: boolean;
  cotisable: boolean;
  proratisable: boolean;
  participeAuNet: boolean;
  systeme: boolean;
  ordre: number;
  actif: boolean;
  compteComptable: string | null;
  description: string | null;
  modifiable: boolean;
}
export type TypeVariablePaie = 'AVANCE' | 'NOTE_DEBIT' | 'AUTRE_RETENUE' | 'REGULARISATION_RETENUE' | 'PRIME_EXCEPTIONNELLE' | 'REGULARISATION_GAIN';
export const LIBELLE_TYPE_VARIABLE: Record<TypeVariablePaie, string> = {
  AVANCE: 'Avance ou quinzaine (retenue)',
  NOTE_DEBIT: 'Note de débit (retenue)',
  AUTRE_RETENUE: 'Autre retenue autorisée',
  REGULARISATION_RETENUE: 'Régularisation en moins (mois antérieur)',
  PRIME_EXCEPTIONNELLE: 'Prime ou gratification exceptionnelle',
  REGULARISATION_GAIN: 'Régularisation en plus (mois antérieur)',
};
export type StatutVariablePaie = 'SAISIE' | 'VALIDEE' | 'REFUSEE' | 'ANNULEE';
export const LIBELLE_STATUT_VARIABLE: Record<StatutVariablePaie, string> = { SAISIE: 'Saisie (à valider)', VALIDEE: 'Validée', REFUSEE: 'Refusée', ANNULEE: 'Annulée' };
export interface VariablePaie {
  id: string;
  periodeId: string;
  agent: { id: string; matricule: string; nom: string; prenom: string; structure: { code: string; libelle: string } | null } | null;
  type: TypeVariablePaie;
  libelleType: string;
  sens: 'GAIN' | 'RETENUE';
  libelle: string;
  montant: number;
  moisOrigine: string | null;
  motif: string;
  pieceId: string | null;
  statut: StatutVariablePaie;
  libelleStatut: string;
  saisiPar: string | null;
  saisiLe: string;
  decidePar: string | null;
  decideLe: string | null;
  motifDecision: string | null;
}
export interface VariablesPeriodePaie {
  periode: { id: string; mois: string; statut: StatutPeriodePresence; figee: boolean };
  variables: VariablePaie[];
  droits: { saisir: boolean; valider: boolean };
}
export type StatutCalculPaie = 'BROUILLON' | 'FIGE' | 'SOUMISE' | 'CONTROLEE_RH' | 'VALIDEE_DRH' | 'VISEE_DGA_CG' | 'APPROUVEE_DF' | 'EN_PAIEMENT' | 'PAYEE' | 'CLOTUREE' | 'ANNULE';
export const LIBELLE_STATUT_CALCUL: Record<StatutCalculPaie, string> = {
  BROUILLON: 'Brouillon',
  FIGE: 'Figé',
  SOUMISE: 'Soumise (contrôle RH)',
  CONTROLEE_RH: 'Contrôlée (RH)',
  VALIDEE_DRH: 'Validée (DRH)',
  VISEE_DGA_CG: 'Visée (DGA, CG)',
  APPROUVEE_DF: 'Approuvée (DF)',
  EN_PAIEMENT: 'En paiement',
  PAYEE: 'Payée',
  CLOTUREE: 'Clôturée',
  ANNULE: 'Annulé',
};
export type EtapeCircuitPaie = 'SOUMISSION' | 'CONTROLE_RH' | 'VALIDATION_DRH' | 'VISA_DGA' | 'VISA_CG' | 'APPROBATION_DF' | 'FED' | 'PAIEMENT' | 'CLOTURE';
export const LIBELLE_ETAPE_CIRCUIT_PAIE: Record<EtapeCircuitPaie, string> = { SOUMISSION: 'Soumission (Superviseur RH)', CONTROLE_RH: 'Contrôle RH (Responsable RH)', VALIDATION_DRH: 'Validation (Directeur RH)', VISA_DGA: 'Visa du DGA Administration et Finances', VISA_CG: 'Visa du Contrôle de gestion', APPROBATION_DF: 'Approbation (Directeur financier)', FED: 'FED de la paie (trésorerie)', PAIEMENT: 'Paiement constaté', CLOTURE: 'Clôture (bulletins publiés, retenues confirmées)' };
export interface DecisionCircuitPaie {
  etape: EtapeCircuitPaie;
  decision: 'VALIDER' | 'RETOURNER' | 'VISER' | 'APPROUVER' | 'SOUMETTRE' | 'FED' | 'PAIEMENT' | 'CLOTURE';
  parId: string;
  par: string;
  le: string;
  commentaire: string | null;
}
export interface MonBulletinPaie {
  calculId: string;
  mois: string;
  numero: number;
  debut: string;
  fin: string;
  clotureLe: string | null;
  payeLe: string | null;
  joursPayes: number;
  totalGains: number;
  totalRetenues: number;
  netAPayer: number;
  cnss: number;
  impotUsd: number;
  retenuesPrets: number;
}
export interface TotauxCalculPaie {
  agents: number;
  bulletins: number;
  bloques: number;
  exclus: number;
  anomalies: number;
  avertissements: number;
  brutPeriode: number;
  heuresSup: number;
  allocationExtralegale: number;
  totalGains: number;
  assietteSociale: number;
  cnss: number;
  remunerationImposable: number;
  impotUsd: number;
  impotCdf: number;
  retenuesPrets: number;
  autresDeductions: number;
  totalRetenues: number;
  netAPayer: number;
  patronales: number;
  coutEmployeur: number;
  parStructure: Array<{ code: string; libelle: string; agents: number; totalGains: number; totalRetenues: number; netAPayer: number; coutEmployeur: number; bloques: number }>;
}
export interface ResumeCalculPaie {
  id: string;
  periode: { id: string; mois: string; debut: string; fin: string; statut: StatutPeriodePresence; datePaiement: string | null };
  numero: number;
  libelle: string;
  simulation: boolean;
  statut: StatutCalculPaie;
  libelleStatut: string;
  tauxChangeCdfUsd: number | null;
  totaux: TotauxCalculPaie;
  exclusions: Array<{ agentId: string; motif: string; nom?: string | null }>;
  calculePar: string | null;
  calculeLe: string;
  figePar: string | null;
  figeLe: string | null;
  annulePar: string | null;
  annuleLe: string | null;
  commentaire: string | null;
  circuit: DecisionCircuitPaie[];
  fedId: string | null;
  payeLe: string | null;
  referencePaiement: string | null;
  cloturePar: string | null;
  clotureLe: string | null;
}
export interface BulletinPaieResume {
  id: string;
  agent: { id: string; matricule: string; nom: string; grade: string | null; cadreDirection: boolean; structure: { code: string; libelle: string } | null };
  joursPayes: number;
  joursDeduits: number;
  brutReference: number;
  brutPeriode: number;
  totalGains: number;
  cnss: number;
  impotUsd: number;
  retenuesPrets: number;
  autresDeductions: number;
  totalRetenues: number;
  netAPayer: number;
  coutEmployeur: number;
  variation: { calculId: string; mois: string; netPrecedent: number; ecart: number; pct: number | null } | null;
  anomalies: number;
  bloquantes: number;
  bloque: boolean;
}
export interface DetailCalculPaie extends ResumeCalculPaie {
  bulletins: BulletinPaieResume[] | null;
  droits: { gerer: boolean; figer: boolean; annuler: boolean; recalculer: boolean; voirBulletins: boolean; etape: EtapeCircuitPaie | null; soumettre: boolean; retirer: boolean; decider: boolean; retourner: boolean; etablirFed: boolean; constaterPaiement: boolean; cloturer: boolean; etats: boolean; virements: boolean; pdf: boolean };
}
export interface LigneBulletinPaie {
  code: string;
  libelle: string;
  bloc: BlocRubriquePaie;
  type: TypeRubriquePaie;
  source: string;
  base: number | null;
  taux: number | null;
  quantite: number | null;
  montant: number;
  montantCdf: number | null;
  imposable: boolean;
  cotisable: boolean;
  net: boolean;
  detail: string | null;
}
export interface BulletinPaieDetail extends BulletinPaieResume {
  calcul: ResumeCalculPaie;
  ficheId: string | null;
  joursHorsContrat: number;
  enfantsRetenus: number;
  personnesChargeFiscales: number;
  assietteSociale: number;
  remunerationImposable: number;
  baseFiscaleCdf: number;
  impotCdf: number;
  patronales: number;
  lignes: LigneBulletinPaie[];
  blocs: Array<{ bloc: BlocRubriquePaie; libelle: string; lignes: LigneBulletinPaie[] }>;
  anomaliesDetail: Array<{ code: string; message: string; bloquante: boolean }>;
  paiement: { mode: string; banque: string | null; compte: string | null; devise: string } | null;
}
export interface ListeCalculsPaie {
  calculs: ResumeCalculPaie[];
  periodes: Array<{ id: string; mois: string; statut: StatutPeriodePresence }>;
  droits: { calculer: boolean; voirBulletins: boolean; validerVariables: boolean };
}
export interface BlocCalculPaie {
  mien: { calculId: string; mois: string; netAPayer: number; clotureLe: string | null } | null;
  dernier: { id: string; mois: string; numero: number; libelle: string; simulation: boolean; statut: StatutCalculPaie; libelleStatut: string; bulletins: number; bloques: number; netAPayer: number; coutEmployeur: number; calculeLe: string; aDecider: boolean } | null;
  periode: { id: string; mois: string; statut: StatutPeriodePresence } | null;
  variablesSaisies: number;
  variablesAValider: number | null;
  rubriquesInactives: number;
  gerer: boolean;
  voirBulletins: boolean;
}

// ------------------------------------------------------------------ Paie : pilotage et paies de comparaison (lot HP-6)
export interface ResumePaiePilotage {
  id: string;
  mois: string;
  numero: number;
  libelle: string;
  statut: StatutCalculPaie;
  libelleStatut: string;
  bulletins: number;
  exclus: number;
  bloques: number;
  anomalies: number;
  avertissements: number;
  brutPeriode: number;
  heuresSup: number;
  allocationExtralegale: number;
  totalGains: number;
  assietteSociale: number;
  cnss: number;
  remunerationImposable: number;
  impotUsd: number;
  impotCdf: number;
  retenuesPrets: number;
  autresDeductions: number;
  totalRetenues: number;
  netAPayer: number;
  patronales: number;
  coutEmployeur: number;
  tauxChangeCdfUsd: number | null;
  calculeLe: string;
  figeLe: string | null;
  payeLe: string | null;
  clotureLe: string | null;
  etape: { code: EtapeCircuitPaie; libelle: string; attendueDe: string; moi: boolean } | null;
}
export interface PilotagePaie {
  jour: string;
  effectifs: { actifs: number; payes: number; horsPaie: number; parCategorie: Array<{ categorie: string; libelle: string; agents: number }>; parStructure: Array<{ code: string; libelle: string; agents: number }>; entrees30j: number; sorties30j: number; finsContratCdd90j: number };
  dossiers: { total: number; valides: number; controles: number; aRegulariser: number; fiches: number; fichesApprouvees: number; fichesControlees: number; fichesARegulariser: number; agentsSansFiche: number };
  periode: { id: string; mois: string; statut: StatutPeriodePresence; debut: string; fin: string; structuresValidees: number; structures: number; variablesSaisies: number; variablesValidees: number } | null;
  paies: ResumePaiePilotage[];
  derniere: (ResumePaiePilotage & { parStructure: Array<{ code: string; libelle: string; agents: number; totalGains: number; netAPayer: number; coutEmployeur: number; part: number }>; obligations: { cnssSalariale: number; cnssPatronale: number; cnssTotal: number; inpp: number; onem: number; iprUsd: number; iprCdf: number; retenuesPrets: number; total: number } | null; variation: { mois: string; net: { ecart: number; pct: number | null }; coutEmployeur: { ecart: number; pct: number | null }; bulletins: number } | null }) | null;
  simulations: number;
  comparaisons: number;
  droits: { voirBulletins: boolean; gerer: boolean };
}
export interface LigneComparaisonPaie {
  matricule: string;
  nom: string | null;
  agentId: string | null;
  statut: 'RAPPROCHE' | 'SANS_BULLETIN' | 'SANS_REFERENCE';
  reference: { brut: number | null; cnss: number | null; ipr: number | null; net: number | null };
  calcule: { brut: number | null; cnss: number | null; ipr: number | null; net: number | null };
  ecarts: { brut: number | null; cnss: number | null; ipr: number | null; net: number | null };
  signale: boolean;
}
export interface ComparaisonPaie {
  calcul: { id: string; mois: string; numero: number; libelle: string; simulation: boolean; statut: StatutCalculPaie };
  comparaison: { id: string; fichier: string; seuil: number; totaux: { lignesFichier: number; rapproches: number; sansBulletin: number; sansReference: number; signales: number; seuil: number; ecartNet: number; ecartBrut: number; ecartCnss: number; ecartIpr: number; ecartAbsoluNet: number }; lignes: LigneComparaisonPaie[] | null; commentaire: string | null; auteur: string | null; le: string } | null;
  droits: { importer: boolean; voirLignes: boolean };
}
